import React, { useState, useEffect, useRef } from 'react';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, convertToRaw, ContentState } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import DOMPurify from 'dompurify';
import { sanitizeProductDescription } from '../utils/sanitizeHtml';
import EmailEditor from 'react-email-editor';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import 'react-quill/dist/quill.snow.css';
import config from '../config';
import PortfolioAdminSection from './PortfolioAdminSection';
import FabricAdminSection from './FabricAdminSection';
import AdminImageCarousel, { dataTransferHasFiles, describeRejectedImage, isImageFile, uploadAdminImage } from './AdminImageCarousel';
import { adminFetch, clearAdminToken, setAdminToken } from '../utils/adminAuth';
import './AdminPage.css';

const API_BASE = config.API_BASE;
const UPLOADS_BASE = config.UPLOADS_BASE || config.API_BASE;
const FULFILLMENT_BADGES = {
  ordered: { label: 'Ordered', color: '#0b57d0', bg: '#e8f0fe', border: '#aecbfa' },
  label_purchased: { label: 'Label Purchased', color: '#8a6d00', bg: '#fff8e1', border: '#ffe082' },
  shipped: { label: 'Shipped', color: '#1b5e20', bg: '#e8f5e9', border: '#a5d6a7' },
  delivered: { label: 'Delivered', color: '#444', bg: '#f3f3f3', border: '#ccc' },
};

function getOrderFulfillmentStage(order, labelLinks = {}) {
  if (order?.fulfillment_stage && FULFILLMENT_BADGES[order.fulfillment_stage]) {
    // Prefer live fields when they are more advanced than a stale fulfillment_stage
  }
  const status = String(order?.status || '').toLowerCase();
  if (status === 'delivered' || order?.delivered_at) return 'delivered';
  if (status === 'shipped') return 'shipped';
  if (
    status === 'label_purchased' ||
    order?.label_url ||
    order?.tracking_number ||
    (order?.id && labelLinks[order.id])
  ) {
    return 'label_purchased';
  }
  if (order?.fulfillment_stage && FULFILLMENT_BADGES[order.fulfillment_stage]) {
    return order.fulfillment_stage;
  }
  return 'ordered';
}

function FulfillmentBadge({ stage }) {
  const badge = FULFILLMENT_BADGES[stage] || FULFILLMENT_BADGES.ordered;
  return (
    <span
      style={{
        color: badge.color,
        fontWeight: 'bold',
        border: `1px solid ${badge.border}`,
        background: badge.bg,
        borderRadius: 4,
        padding: '2px 8px',
        display: 'inline-block',
        whiteSpace: 'nowrap',
      }}
    >
      {badge.label}
    </span>
  );
}

const ADMIN_SECTIONS = [
  { id: 'orders', num: '01', label: 'Orders', desc: 'Manage orders, shipping labels, and fulfillment.' },
  { id: 'products', num: '02', label: 'Products', desc: 'Add, edit, and manage store inventory.' },
  { id: 'categories', num: '03', label: 'Categories', desc: 'Organize products by category.' },
  { id: 'discounts', num: '04', label: 'Discount Codes', desc: 'Create promo codes with expiry or usage limits.' },
  { id: 'portfolio', num: '05', label: 'Portfolio', desc: 'Edit portfolio projects and CV content.' },
  { id: 'email-templates', num: '06', label: 'Email Templates', desc: 'Configure transactional and newsletter emails.' },
  { id: 'locked-page', num: '07', label: 'Locked Page', desc: 'Toggle site lock and coming-soon mode.' },
  { id: 'fabrics', num: '08', label: 'Fabric Sources', desc: 'Manage external fabric buying links.' },
];

// Custom Checkbox Component
const CustomCheckbox = ({ checked, onChange }) => {
  return (
    <div
      onClick={() => onChange(!checked)}
      style={{
        width: 20,
        height: 20,
        border: '1px solid #000',
        borderRadius: 0,
        backgroundColor: checked ? '#000' : '#fff',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        flexShrink: 0
      }}
    >
      {checked && (
        <span style={{
          color: '#fff',
          fontSize: 14,
          fontWeight: 'bold',
          lineHeight: 1,
          userSelect: 'none'
        }}>✓</span>
      )}
    </div>
  );
};

// Add this custom style map for highlights
// Remove highlightColors, customStyleMap, and removeHighlight function

export default function AdminPage() {
  const [authed, setAuthed] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [rates, setRates] = useState({}); // { orderId: [rates] }
  const [labelLinks, setLabelLinks] = useState({}); // { orderId: { url, tracking } }
  const [rateLoading, setRateLoading] = useState({}); // { orderId: true/false }
  const [purchaseLoading, setPurchaseLoading] = useState({}); // { orderId: true/false }
  const [rateError, setRateError] = useState({}); // { orderId: error }
  const [selectedRate, setSelectedRate] = useState({}); // { orderId: rateObjectId }
  const [bulkSelected, setBulkSelected] = useState([]); // array of order IDs
  const [bulkRate, setBulkRate] = useState('');
  const [bulkRatesList, setBulkRatesList] = useState([]); // available rates for bulk
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkStatus, setBulkStatus] = useState({}); // { orderId: 'pending'|'success'|'error' }
  // Market orders: manual tracking + "purchased to ship" (no Shippo)
  const [marketOrderForm, setMarketOrderForm] = useState({}); // { orderId: { tracking_number, carrier, product_purchased_for_customer } }
  const [marketOrderSaving, setMarketOrderSaving] = useState({}); // { orderId: boolean }
  const [oldOrdersOpen, setOldOrdersOpen] = useState(false);
  const [trackingSyncing, setTrackingSyncing] = useState(false);

  // Product management state
  const [products, setProducts] = useState([]);
  const [productLoading, setProductLoading] = useState(false);
  const [productError, setProductError] = useState('');
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({ name: '', slug: '', price: '', description: '', admin_notes: '', image: '', images: [], category_ids: [], show_on_commission_page: false, show_on_market_page: false });
  const [productFilter, setProductFilter] = useState('all'); // 'all', 'in-stock', 'out-of-stock'
  
  // Size and inventory management state
  const [showSizeManager, setShowSizeManager] = useState(false);
  const [editingProductSizes, setEditingProductSizes] = useState(null);
  const [sizeForm, setSizeForm] = useState({ size: '', quantity: 0, preorder: false, made_to_order: false });
  const [sizeLoading, setSizeLoading] = useState(false);
  const [sizeError, setSizeError] = useState('');

  // Category management state
  const [categories, setCategories] = useState([]);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [categoryError, setCategoryError] = useState('');
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');

  // Discount code management state
  const [discountCodes, setDiscountCodes] = useState([]);
  const [discountLoading, setDiscountLoading] = useState(false);
  const [discountError, setDiscountError] = useState('');
  const [showDiscountForm, setShowDiscountForm] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [discountForm, setDiscountForm] = useState({
    code: '',
    type: 'percent',
    value: '',
    limitMode: 'none',
    expires_at: '',
    max_uses: '',
    is_active: true,
  });

  // Commission items management state
  const [commissionItems, setCommissionItems] = useState([]);
  const [commissionLoading, setCommissionLoading] = useState(false);
  const [commissionError, setCommissionError] = useState('');
  const [showCommissionForm, setShowCommissionForm] = useState(false);
  const [editingCommission, setEditingCommission] = useState(null);
  const [commissionForm, setCommissionForm] = useState({ title: '', description: '', image: '', images: [], slug: '', display_order: 0 });

  // Drag and drop state
  const [draggedItem, setDraggedItem] = useState(null);

  // Draft.js state for description
  const [editorState, setEditorState] = useState(EditorState.createEmpty());

  // Sync editorState with product description when opening the product form
  useEffect(() => {
    if (showProductForm) {
      if (editingProduct && editingProduct.description) {
        // Convert HTML to Draft.js ContentState
        const blocksFromHtml = htmlToDraft(editingProduct.description);
        if (blocksFromHtml && blocksFromHtml.contentBlocks) {
          const contentState = ContentState.createFromBlockArray(blocksFromHtml.contentBlocks, blocksFromHtml.entityMap);
          setEditorState(EditorState.createWithContent(contentState));
        } else {
          setEditorState(EditorState.createEmpty());
        }
      } else {
        setEditorState(EditorState.createEmpty());
      }
    }
  }, [showProductForm, editingProduct]);

  // Tab state for admin sections
  const [adminTab, setAdminTab] = useState('orders'); // 'orders' or 'products' or 'categories' or 'portfolio' or 'email-templates' or 'locked-page'

  useEffect(() => {
    adminFetch(`${API_BASE}/api/admin/session`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.authenticated) {
          if (data.token) setAdminToken(data.token);
          setAuthed(true);
        }
      })
      .catch(() => {});
  }, []);  
  // Stored email templates (list + edit)
  const [storedTemplatesList, setStoredTemplatesList] = useState([]);
  const [storedTemplatesLoading, setStoredTemplatesLoading] = useState(false);
  const [editingStoredTemplate, setEditingStoredTemplate] = useState(null); // { id, title, trigger, subject, html } or null
  const [storedTemplateSaveResult, setStoredTemplateSaveResult] = useState(null);
  const [sendTestEmailForId, setSendTestEmailForId] = useState(null);
  const [sendTestEmailAddress, setSendTestEmailAddress] = useState('');
  const [manualSendForId, setManualSendForId] = useState(null);
  const [manualSendTo, setManualSendTo] = useState('');
  
  // Test Email state
  const [testEmailLoading, setTestEmailLoading] = useState(false);
  const [testEmailResult, setTestEmailResult] = useState(null);
  const [testEmailAddress, setTestEmailAddress] = useState('');

  // Locked Page state
  const [isLocked, setIsLocked] = useState(false);
  const [lockedLoading, setLockedLoading] = useState(false);
  const [lockedError, setLockedError] = useState('');
  
  // Email Signups state
  const [emailSignups, setEmailSignups] = useState([]);
  const [signupsLoading, setSignupsLoading] = useState(false);
  const [signupsError, setSignupsError] = useState('');
  
  // Newsletter state
  const [newsletterSubject, setNewsletterSubject] = useState('');
  const [newsletterMessage, setNewsletterMessage] = useState('');
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterResult, setNewsletterResult] = useState(null);

  // Shippo settings (store ship-from address + default parcel)
  const [shippoSettingsLoading, setShippoSettingsLoading] = useState(false);
  const [shippoSettingsSaving, setShippoSettingsSaving] = useState(false);
  const [shippoSettingsResult, setShippoSettingsResult] = useState(null);
  const [shippoSettings, setShippoSettings] = useState({
    from_name: '',
    from_email: '',
    from_phone: '',
    from_street1: '',
    from_city: '',
    from_state: '',
    from_zip: '',
    from_country: 'US',
    parcel_length: '',
    parcel_width: '',
    parcel_height: '',
    parcel_weight: ''
  });
  
  // Email Templates state
  const [emailTemplates, setEmailTemplates] = useState({
    orderConfirmation: { subject: '', html: '' },
    newsletter: { subject: '', html: '' }
  });
  const [selectedTemplate, setSelectedTemplate] = useState('orderConfirmation');
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [templateResult, setTemplateResult] = useState(null);
  const editorRef = useRef(null);
  const emailEditorRef = useRef(null);
  const [showVisualBuilder, setShowVisualBuilder] = useState(false);

  // Test Email function
  const testEmail = async () => {
    setTestEmailLoading(true);
    setTestEmailResult(null);
    
    try {
      const response = await adminFetch(`${API_BASE}/api/admin/test-email`, {
        credentials: 'include',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: testEmailAddress || 'justingmbusiness@gmail.com',
          templateType: selectedTemplate // Send the currently selected template
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setTestEmailResult({ 
          success: true, 
          message: `Test ${selectedTemplate === 'orderConfirmation' ? 'order confirmation' : 'newsletter'} email sent successfully! Check your inbox.`,
          details: data.details 
        });
      } else {
        setTestEmailResult({ 
          success: false, 
          message: data.error || 'Failed to send test email' 
        });
      }
    } catch (error) {
      setTestEmailResult({ 
        success: false, 
        message: 'Error: ' + error.message 
      });
    }
    
    setTestEmailLoading(false);
  };

  // Locked Page functions
  const fetchLockedState = async () => {
    setLockedLoading(true);
    try {
      const response = await adminFetch(`${API_BASE}/api/locked-state`);
      const data = await response.json();
      if (data.success) {
        setIsLocked(data.isLocked);
        setLockedError('');
      } else {
        setLockedError('Failed to fetch locked state');
      }
    } catch (error) {
      setLockedError('Failed to fetch locked state');
    }
    setLockedLoading(false);
  };

  const updateLockedState = async (newState) => {
    setLockedLoading(true);
    setLockedError('');
    try {
      const response = await adminFetch(`${API_BASE}/api/admin/locked-state`, {
        credentials: 'include',
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ isLocked: newState })
      });
      
      const data = await response.json();
      if (data.success) {
        setIsLocked(newState);
        setLockedError('');
      } else {
        setLockedError(data.error || 'Failed to update locked state');
      }
    } catch (error) {
      setLockedError('Failed to update locked state');
    }
    setLockedLoading(false);
  };

  // Email Signups functions
  const fetchEmailSignups = async () => {
    setSignupsLoading(true);
    setSignupsError('');
    try {
      const response = await adminFetch(`${API_BASE}/api/admin/email-signups`, {
        credentials: 'include',
        headers: {
        }
      });
      if (response.status === 401 || response.status === 403) {
        handleAdminAuthFailure();
        setSignupsLoading(false);
        return;
      }
      const data = await response.json();
      if (data.success) {
        setEmailSignups(data.signups);
      } else {
        setSignupsError(data.error || 'Failed to fetch email signups');
      }
    } catch (error) {
      setSignupsError('Failed to fetch email signups');
    }
    setSignupsLoading(false);
  };

  const deleteEmailSignup = async (id) => {
    if (!window.confirm('Are you sure you want to delete this email signup?')) return;
    
    try {
      const response = await adminFetch(`${API_BASE}/api/admin/email-signups/${id}`, {
        credentials: 'include',
        method: 'DELETE',
        headers: {
        }
      });
      const data = await response.json();
      if (data.success) {
        await fetchEmailSignups();
        setSignupsError('');
      } else {
        setSignupsError(data.error || 'Failed to delete email signup');
      }
    } catch (error) {
      setSignupsError('Failed to delete email signup');
    }
  };

  // Newsletter functions
  const sendNewsletter = async () => {
    if (!newsletterSubject.trim() || !newsletterMessage.trim()) {
      alert('Please enter both subject and message');
      return;
    }
    
    if (emailSignups.length === 0) {
      alert('No email signups to send to');
      return;
    }
    
    if (!window.confirm(`Send newsletter to ${emailSignups.length} recipients?`)) {
      return;
    }
    
    setNewsletterLoading(true);
    setNewsletterResult(null);
    
    try {
      const response = await adminFetch(`${API_BASE}/api/admin/send-newsletter`, {
        credentials: 'include',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subject: newsletterSubject,
          message: newsletterMessage
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setNewsletterResult({ 
          success: true, 
          message: `Newsletter sent successfully to ${data.sent} recipients${data.failed > 0 ? ` (${data.failed} failed)` : ''}` 
        });
        setNewsletterSubject('');
        setNewsletterMessage('');
      } else {
        setNewsletterResult({ 
          success: false, 
          message: data.error || 'Failed to send newsletter' 
        });
      }
    } catch (error) {
      setNewsletterResult({ 
        success: false, 
        message: 'Failed to send newsletter: ' + error.message 
      });
    } finally {
      setNewsletterLoading(false);
    }
  };

  const fetchShippoSettings = async () => {
    setShippoSettingsLoading(true);
    setShippoSettingsResult(null);
    try {
      const res = await adminFetch(`${API_BASE}/api/admin/shippo-settings`, {
        credentials: 'include'
      });
      if (res.status === 401 || res.status === 403) {
        handleAdminAuthFailure();
        setShippoSettingsLoading(false);
        return;
      }
      const data = await res.json();
      if (data.success) {
        setShippoSettings(prev => ({ ...prev, ...(data.settings || {}) }));
      } else {
        setShippoSettingsResult({ success: false, message: data.error || 'Failed to load Shippo settings' });
      }
    } catch (e) {
      setShippoSettingsResult({ success: false, message: 'Failed to load Shippo settings' });
    }
    setShippoSettingsLoading(false);
  };

  const saveShippoSettings = async () => {
    setShippoSettingsSaving(true);
    setShippoSettingsResult(null);
    try {
      const res = await adminFetch(`${API_BASE}/api/admin/shippo-settings`, {
        credentials: 'include',
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(shippoSettings)
      });
      const data = await res.json();
      setShippoSettingsResult({ success: data.success, message: data.success ? 'Saved Shippo settings.' : (data.error || 'Failed to save') });
    } catch (e) {
      setShippoSettingsResult({ success: false, message: 'Failed to save Shippo settings' });
    }
    setShippoSettingsSaving(false);
  };

  // Email Templates functions
  const fetchEmailTemplates = async () => {
    setTemplateLoading(true);
    try {
      const response = await adminFetch(`${API_BASE}/api/admin/email-templates`, {
        credentials: 'include',
        headers: {
        }
      });
      const data = await response.json();
      if (data.success) {
        setEmailTemplates(data.templates);
      }
    } catch (error) {
      console.error('Error fetching email templates:', error);
    } finally {
      setTemplateLoading(false);
    }
  };

  const saveEmailTemplate = async () => {
    setTemplateSaving(true);
    setTemplateResult(null);
    try {
      const response = await adminFetch(`${API_BASE}/api/admin/email-templates`, {
        credentials: 'include',
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          templateType: selectedTemplate,
          subject: emailTemplates[selectedTemplate].subject,
          html: emailTemplates[selectedTemplate].html
        })
      });
      const data = await response.json();
      if (data.success) {
        setTemplateResult({ success: true, message: 'Email template saved successfully!' });
      } else {
        setTemplateResult({ success: false, message: data.error || 'Failed to save template' });
      }
    } catch (error) {
      setTemplateResult({ success: false, message: 'Failed to save template: ' + error.message });
    } finally {
      setTemplateSaving(false);
    }
  };

  const downloadEmailSignups = () => {
    if (emailSignups.length === 0) {
      alert('No email signups to download');
      return;
    }

    // Create CSV content
    const headers = ['Email', 'Signed Up'];
    const rows = emailSignups.map(signup => {
      const date = new Date(signup.created_at).toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
      return [signup.email, date];
    });

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `email-signups-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Category management functions
  const fetchCategories = async () => {
    setCategoryLoading(true);
    try {
      const res = await adminFetch(`${API_BASE}/api/categories`);
      const data = await res.json();
      if (data.success) {
        setCategories(data.categories);
        setCategoryError('');
      } else {
        setCategoryError('Failed to fetch categories.');
      }
    } catch (err) {
      setCategoryError('Failed to fetch categories.');
    }
    setCategoryLoading(false);
  };

  const addCategory = async (e) => {
    e.preventDefault();
    if (!newCategoryName.trim()) return;
    
    try {
      const res = await adminFetch(`${API_BASE}/api/admin/categories`, {
        credentials: 'include',
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: newCategoryName.trim() })
      });
      const data = await res.json();
      if (data.success) {
        // Refresh categories list to ensure consistency
        await fetchCategories();
        setNewCategoryName('');
        setShowCategoryForm(false);
        setCategoryError('');
      } else {
        setCategoryError(data.error || 'Failed to add category.');
      }
    } catch (err) {
      setCategoryError('Failed to add category.');
    }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    
    try {
      const categoryId = id || (typeof id === 'object' ? (id._id || id.id) : id);
      const res = await adminFetch(`${API_BASE}/api/admin/categories/${categoryId}`, {
        credentials: 'include',
        method: 'DELETE',
        headers: {
        }
      });
      const data = await res.json();
      if (data.success) {
        // Refresh categories list to ensure consistency
        await fetchCategories();
        setCategoryError('');
      } else {
        setCategoryError(data.error || 'Failed to delete category.');
      }
    } catch (err) {
      console.error('Error deleting category:', err);
      setCategoryError('Failed to delete category.');
    }
  };

  const resetDiscountForm = () => {
    setDiscountForm({
      code: '',
      type: 'percent',
      value: '',
      limitMode: 'none',
      expires_at: '',
      max_uses: '',
      is_active: true,
    });
    setEditingDiscount(null);
    setShowDiscountForm(false);
    setDiscountError('');
  };

  const openCreateDiscountForm = () => {
    resetDiscountForm();
    setShowDiscountForm(true);
  };

  const openEditDiscountForm = (discount) => {
    let limitMode = 'none';
    const hasDate = Boolean(discount.expires_at);
    const hasUses = discount.max_uses != null;
    if (hasDate && hasUses) limitMode = 'both';
    else if (hasDate) limitMode = 'date';
    else if (hasUses) limitMode = 'uses';

    setEditingDiscount(discount);
    setDiscountForm({
      code: discount.code || '',
      type: discount.type || 'percent',
      value: discount.value ?? '',
      limitMode,
      expires_at: discount.expires_at ? new Date(discount.expires_at).toISOString().slice(0, 16) : '',
      max_uses: discount.max_uses ?? '',
      is_active: discount.is_active !== false,
    });
    setShowDiscountForm(true);
    setDiscountError('');
  };

  const buildDiscountPayload = (form) => {
    const payload = {
      code: form.code.trim().toUpperCase(),
      type: form.type,
      value: Number(form.value),
      is_active: form.is_active,
      expires_at: null,
      max_uses: null,
    };
    if (form.limitMode === 'date' || form.limitMode === 'both') {
      payload.expires_at = form.expires_at ? new Date(form.expires_at).toISOString() : null;
    }
    if (form.limitMode === 'uses' || form.limitMode === 'both') {
      payload.max_uses = form.max_uses ? parseInt(form.max_uses, 10) : null;
    }
    return payload;
  };

  const fetchDiscountCodes = async () => {
    setDiscountLoading(true);
    try {
      const res = await adminFetch(`${API_BASE}/api/admin/discount-codes`, { credentials: 'include' });
      if (res.status === 401 || res.status === 403) {
        handleAdminAuthFailure();
        setDiscountLoading(false);
        return;
      }
      const data = await res.json();
      if (data.success) {
        setDiscountCodes(data.discountCodes || []);
        setDiscountError('');
      } else {
        setDiscountError(data.error || 'Failed to fetch discount codes.');
      }
    } catch {
      setDiscountError('Failed to fetch discount codes.');
    }
    setDiscountLoading(false);
  };

  const saveDiscountCode = async (e) => {
    e.preventDefault();
    setDiscountError('');

    if (!discountForm.code.trim()) {
      setDiscountError('Code is required.');
      return;
    }
    if (!discountForm.value || Number(discountForm.value) <= 0) {
      setDiscountError('Enter a valid discount value.');
      return;
    }
    if (discountForm.type === 'percent' && Number(discountForm.value) > 100) {
      setDiscountError('Percentage cannot exceed 100.');
      return;
    }
    if ((discountForm.limitMode === 'date' || discountForm.limitMode === 'both') && !discountForm.expires_at) {
      setDiscountError('Choose an expiration date.');
      return;
    }
    if ((discountForm.limitMode === 'uses' || discountForm.limitMode === 'both') && (!discountForm.max_uses || parseInt(discountForm.max_uses, 10) < 1)) {
      setDiscountError('Enter a max use count of at least 1.');
      return;
    }

    try {
      const payload = buildDiscountPayload(discountForm);
      const isEdit = Boolean(editingDiscount?.id);
      const url = isEdit
        ? `${API_BASE}/api/admin/discount-codes/${editingDiscount.id}`
        : `${API_BASE}/api/admin/discount-codes`;
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        await fetchDiscountCodes();
        resetDiscountForm();
      } else {
        setDiscountError(data.error || 'Failed to save discount code.');
      }
    } catch {
      setDiscountError('Failed to save discount code.');
    }
  };

  const deleteDiscountCode = async (id) => {
    if (!window.confirm('Delete this discount code?')) return;
    try {
      const res = await adminFetch(`${API_BASE}/api/admin/discount-codes/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json();
      if (data.success) {
        await fetchDiscountCodes();
        setDiscountError('');
      } else {
        setDiscountError(data.error || 'Failed to delete discount code.');
      }
    } catch {
      setDiscountError('Failed to delete discount code.');
    }
  };

  const formatDiscountValue = (discount) => (
    discount.type === 'percent'
      ? `${discount.value}% off`
      : `$${Number(discount.value).toFixed(2)} off`
  );

  const formatDiscountLimits = (discount) => {
    const parts = [];
    if (discount.max_uses != null) {
      parts.push(`${discount.use_count}/${discount.max_uses} uses`);
    } else {
      parts.push(`${discount.use_count} uses (no max)`);
    }
    if (discount.expires_at) {
      parts.push(`until ${new Date(discount.expires_at).toLocaleString()}`);
    }
    if (discount.max_uses == null && !discount.expires_at) {
      return 'No expiration limit';
    }
    return parts.join(' · ');
  };

  const formatDiscountStatusLabel = (discount) => {
    if (discount.status === 'disabled') return 'Disabled';
    if (discount.status === 'expired') return 'Expired';
    if (discount.status === 'used_up') return 'Used up';
    return 'Active';
  };

  // Commission Items Functions
  const fetchCommissionItems = async () => {
    setCommissionLoading(true);
    try {
      const res = await adminFetch(`${API_BASE}/api/commission-items`);
      const data = await res.json();
      if (data.success) {
        setCommissionItems(data.items);
        setCommissionError('');
      } else {
        setCommissionError('Failed to load commission items.');
      }
    } catch (err) {
      setCommissionError('Failed to load commission items.');
    }
    setCommissionLoading(false);
  };

  const openAddCommission = () => {
    setCommissionForm({ title: '', description: '', image: '', images: [], slug: '', display_order: 0 });
    setEditingCommission(null);
    setShowCommissionForm(true);
    setCommissionError('');
  };

  const openEditCommission = (item) => {
    // Parse images if it's a string
    let images = [];
    if (item.images) {
      try {
        images = typeof item.images === 'string' ? JSON.parse(item.images) : item.images;
      } catch {
        images = item.image ? [item.image] : [];
      }
    } else if (item.image) {
      images = [item.image];
    }
    
    setCommissionForm({
      title: item.title,
      description: item.description || '',
      image: item.image || (images.length > 0 ? images[0] : ''),
      images: images,
      slug: item.slug || '',
      display_order: item.display_order || 0
    });
    setEditingCommission(item);
    setShowCommissionForm(true);
    setCommissionError('');
  };

  const handleCommissionSubmit = async (e) => {
    e.preventDefault();
    
    if (!commissionForm.title.trim()) {
      setCommissionError('Title is required.');
      return;
    }

    if (commissionForm.images.length === 0) {
      setCommissionError('At least one image is required.');
      return;
    }

    setCommissionLoading(true);
    
    try {
      const method = editingCommission ? 'PUT' : 'POST';
      const url = editingCommission 
        ? `${API_BASE}/api/admin/commission-items/${editingCommission.id}`
        : `${API_BASE}/api/admin/commission-items`;
      
      const res = await fetch(url, {
        method,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(commissionForm)
      });
      
      const data = await res.json();
      
      if (data.success) {
        await fetchCommissionItems();
        setShowCommissionForm(false);
        setCommissionForm({ title: '', description: '', image: '', images: [], slug: '', display_order: 0 });
        setEditingCommission(null);
        setCommissionError('');
      } else {
        setCommissionError(data.error || 'Failed to save commission item.');
      }
    } catch (err) {
      setCommissionError('Failed to save commission item.');
    }
    
    setCommissionLoading(false);
  };

  const deleteCommissionItem = async (id) => {
    if (!window.confirm('Are you sure you want to delete this commission item?')) return;
    
    setCommissionLoading(true);
    try {
      const res = await adminFetch(`${API_BASE}/api/admin/commission-items/${id}`, {
        credentials: 'include',
        method: 'DELETE',
        headers: {
        }
      });
      const data = await res.json();
      if (data.success) {
        setCommissionItems(prev => prev.filter(item => item.id !== id));
        setCommissionError('');
      } else {
        setCommissionError('Failed to delete commission item.');
      }
    } catch (err) {
      setCommissionError('Failed to delete commission item.');
    }
    setCommissionLoading(false);
  };

  const handleCommissionImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const url = await uploadAdminImage(file);
      setCommissionForm(prev => {
        const newImages = [...prev.images, url];
        return {
          ...prev,
          images: newImages,
          image: newImages[0]
        };
      });
    } catch (err) {
      alert(err.message || 'Failed to upload image');
    }
  };

  const removeCommissionImage = (indexToRemove) => {
    setCommissionForm(prev => {
      const newImages = prev.images.filter((_, index) => index !== indexToRemove);
      return {
        ...prev,
        images: newImages,
        image: newImages.length > 0 ? newImages[0] : ''
      };
    });
  };


  useEffect(() => {
    if (authed) {
      setLoading(true);
      adminFetch(`${API_BASE}/api/admin/orders`, {
        credentials: 'include',
        headers: {
        }
      })
        .then(res => {
          if (res.status === 401 || res.status === 403) {
            handleAdminAuthFailure();
            setLoading(false);
            return null;
          }
          return res.json();
        })
        .then(data => {
          if (!data) return;
          if (data.success) {
            setOrders(data.orders);
            setFetchError('');
            // Refresh fulfillment from carrier tracking (non-blocking)
            setTrackingSyncing(true);
            adminFetch(`${API_BASE}/api/admin/orders/sync-tracking`, {
              credentials: 'include',
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
            })
              .then((res) => (res.ok ? res.json() : null))
              .then((sync) => {
                if (!sync?.success || !Array.isArray(sync.orders) || sync.orders.length === 0) return;
                const map = Object.fromEntries(sync.orders.map((o) => [o.id, o]));
                setOrders((prev) => prev.map((o) => (map[o.id] ? { ...o, ...map[o.id] } : o)));
              })
              .catch(() => {})
              .finally(() => setTrackingSyncing(false));
          } else {
            setFetchError('Failed to fetch orders.');
          }
          setLoading(false);
        })
        .catch(() => {
          setFetchError('Failed to fetch orders.');
          setLoading(false);
        });
      
      // Fetch categories
      fetchCategories();
      
      // Fetch products
      fetchProducts();
    }
  }, [authed]);
  
  // Fetch categories when categories tab is clicked (if not already loaded)
  useEffect(() => {
    if (authed && adminTab === 'categories') {
      fetchCategories();
    }
    if (authed && adminTab === 'discounts') {
      fetchDiscountCodes();
    }
  }, [authed, adminTab]);

  useEffect(() => {
    if (authed && adminTab === 'locked-page') {
      fetchLockedState();
    }
    if (authed && adminTab === 'email-templates') {
      fetchStoredTemplatesList();
      fetchEmailSignups();
      fetchShippoSettings();
    }
  }, [authed, adminTab]);

  const handleAdminAuthFailure = () => {
    clearAdminToken();
    setAuthed(false);
    setError('Session expired. Please log in again.');
  };

  const fetchStoredTemplatesList = async () => {
    setStoredTemplatesLoading(true);
    try {
      const res = await adminFetch(`${API_BASE}/api/admin/email-templates/list`, {
        credentials: 'include'
      });
      if (res.status === 401 || res.status === 403) {
        handleAdminAuthFailure();
        setStoredTemplatesLoading(false);
        return;
      }
      const data = await res.json();
      if (data.success) setStoredTemplatesList(data.templates || []);
    } catch (e) {
      console.error(e);
    }
    setStoredTemplatesLoading(false);
  };
  
  // Update iframe content when template changes
  useEffect(() => {
    const iframe = document.getElementById('email-editor-iframe');
    if (iframe && iframe.contentDocument && iframe.contentDocument.body) {
      iframe.contentDocument.body.innerHTML = emailTemplates[selectedTemplate]?.html || '';
      iframe.contentDocument.body.style.direction = 'ltr';
      iframe.contentDocument.body.style.textAlign = 'left';
    }
  }, [selectedTemplate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      const response = await adminFetch(`${API_BASE}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password })
      });
      
      const data = await response.json();
      
      if (data.success) {
        if (data.token) setAdminToken(data.token);
        setAuthed(true);
        setError('');
        setPassword('');
      } else {
        setError(data.error || 'Invalid password');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Login failed. Please try again.');
    }
  };

  const handleLogout = async () => {
    try {
      await adminFetch(`${API_BASE}/api/admin/logout`, { method: 'POST' });
    } catch {
      // ignore
    }
    clearAdminToken();
    setAuthed(false);
    setPassword('');
  };
  const fetchRates = async (order) => {
    setRateLoading(r => ({ ...r, [order.id]: true }));
    setRateError(e => ({ ...e, [order.id]: '' }));
    try {
      const res = await adminFetch(`${API_BASE}/api/shippo/rates`, {
        credentials: 'include',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ orderId: order.id })
      });
      const data = await res.json();
      if (data.success) {
        setRates(r => ({ ...r, [order.id]: data.rates }));
        // Auto-select USPS Ground Advantage if available
        const groundAdv = data.rates.find(rate =>
          rate.provider === 'USPS' &&
          (rate.servicelevel?.name?.toLowerCase().includes('ground advantage') || rate.servicelevel?.token === 'usps_ground_advantage')
        );
        if (groundAdv) {
          setSelectedRate(sr => ({ ...sr, [order.id]: groundAdv.object_id }));
        } else {
          setSelectedRate(sr => ({ ...sr, [order.id]: '' }));
        }
      } else {
        setRateError(e => ({ ...e, [order.id]: data.error || 'Failed to fetch rates' }));
      }
    } catch (err) {
      setRateError(e => ({ ...e, [order.id]: 'Failed to fetch rates' }));
    }
    setRateLoading(r => ({ ...r, [order.id]: false }));
  };

  const purchaseLabel = async (orderId, rateObjectId) => {
    setPurchaseLoading(l => ({ ...l, [orderId]: true }));
    try {
      const res = await adminFetch(`${API_BASE}/api/shippo/purchase`, {
        credentials: 'include',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ orderId, rate_object_id: rateObjectId })
      });
      const data = await res.json().catch(() => ({}));
      if (data.success && data.label?.label_url) {
        setLabelLinks(l => ({ ...l, [orderId]: { url: data.label.label_url, tracking: data.label.tracking_number } }));
        // Refresh order row fields from server response when available
        if (data.order) {
          setOrders(prev => prev.map(o => o.id === orderId ? {
            ...o,
            label_url: data.order.label_url || o.label_url,
            tracking_number: data.order.tracking_number || o.tracking_number,
            tracking_url: data.order.tracking_url || o.tracking_url,
            carrier: data.order.carrier || o.carrier,
            status: data.order.status || 'label_purchased',
            fulfillment_stage: data.order.fulfillment_stage || 'label_purchased',
          } : o));
        }
      } else {
        const rawError = data.error;
        const errorText = typeof rawError === 'string'
          ? rawError
          : (rawError && typeof rawError === 'object' ? JSON.stringify(rawError) : 'Unknown error');
        alert('Failed to purchase label: ' + errorText + '\n\nTip: click View Rates again, then Buy Label with a fresh rate.');
      }
    } catch (err) {
      alert('Failed to purchase label. Check your connection and try again.');
    }
    setPurchaseLoading(l => ({ ...l, [orderId]: false }));
  };

  // Sort orders by created_at descending (newest first)
  const sortedOrders = [...orders].sort((a, b) => {
    const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
    const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
    return bTime - aTime;
  });

  const formatOrderItem = (item) => {
    const name = item?.name || item?.product_name || item?.title || 'Unknown product';
    const size = item?.size || item?.selectedSize || item?.product_size || '';
    const quantity = item?.quantity ?? 1;
    const sizePart = size ? ` (${size})` : '';
    return `${name}${sizePart} x${quantity}`;
  };

  const activeOrders = sortedOrders.filter(
    (order) => getOrderFulfillmentStage(order, labelLinks) !== 'delivered'
  );
  const deliveredOrders = sortedOrders.filter(
    (order) => getOrderFulfillmentStage(order, labelLinks) === 'delivered'
  );

  // Helper: get all orders without a label (exclude market orders - they use manual tracking)
  const ordersWithoutLabel = activeOrders.filter(order => !order.is_market_order && !(order.label_url || labelLinks[order.id]));

  // Initialize market order form from order data when orders load
  useEffect(() => {
    setMarketOrderForm(prev => {
      const next = { ...prev };
      let changed = false;
      orders.filter(o => o.is_market_order).forEach(o => {
        if (!(o.id in next)) {
          next[o.id] = { tracking_number: o.tracking_number || '', carrier: o.carrier || '', product_purchased_for_customer: o.product_purchased_for_customer || false };
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [orders]);

  const saveMarketOrderTracking = async (orderId) => {
    const form = marketOrderForm[orderId];
    if (!form) return;
    setMarketOrderSaving(s => ({ ...s, [orderId]: true }));
    try {
      const res = await adminFetch(`${API_BASE}/api/admin/orders/${orderId}/market-tracking`, {
        credentials: 'include',
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tracking_number: form.tracking_number || '', carrier: form.carrier || '', product_purchased_for_customer: form.product_purchased_for_customer })
      });
      const data = await res.json();
      if (data.success && data.order) {
        setOrders(prev => prev.map(o => o.id === orderId ? {
          ...o,
          tracking_number: data.order.tracking_number,
          tracking_url: data.order.tracking_url,
          carrier: data.order.carrier,
          product_purchased_for_customer: data.order.product_purchased_for_customer,
          status: data.order.status || o.status,
          fulfillment_stage: data.order.fulfillment_stage || o.fulfillment_stage,
          delivered_at: data.order.delivered_at || o.delivered_at,
          label_url: data.order.label_url || o.label_url,
        } : o));
        setMarketOrderForm(prev => ({ ...prev, [orderId]: { tracking_number: data.order.tracking_number || '', carrier: data.order.carrier || '', product_purchased_for_customer: data.order.product_purchased_for_customer || false } }));
      }
    } catch (e) {
      console.error('Failed to save market order tracking', e);
    }
    setMarketOrderSaving(s => ({ ...s, [orderId]: false }));
  };

  // Bulk: fetch rates for all selected orders and find common rates
  const fetchBulkRates = async () => {
    setBulkLoading(true);
    let allRates = [];
    for (const order of ordersWithoutLabel) {
      try {
              const res = await adminFetch(`${API_BASE}/api/shippo/rates`, {
        credentials: 'include',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ orderId: order.id })
      });
        const data = await res.json();
        if (data.success) {
          allRates.push(data.rates);
        } else {
          allRates.push([]);
        }
      } catch {
        allRates.push([]);
      }
    }
    // Find common rates by servicelevel token
    const rateMap = {};
    allRates.forEach(rates => {
      rates.forEach(rate => {
        if (!rateMap[rate.servicelevel?.token]) {
          rateMap[rate.servicelevel?.token] = { ...rate, count: 1 };
        } else {
          rateMap[rate.servicelevel?.token].count++;
        }
      });
    });
    // Only keep rates available for all selected orders
    const commonRates = Object.values(rateMap).filter(r => r.count === allRates.length);
    setBulkRatesList(commonRates);
    // Auto-select USPS Ground Advantage if available
    const groundAdv = commonRates.find(rate =>
      rate.provider === 'USPS' &&
      (rate.servicelevel?.name?.toLowerCase().includes('ground advantage') || rate.servicelevel?.token === 'usps_ground_advantage')
    );
    if (groundAdv) {
      setBulkRate(groundAdv.object_id);
    } else {
      setBulkRate(commonRates[0]?.object_id || '');
    }
    setBulkLoading(false);
  };

  // Bulk: purchase labels for all selected orders
  const handleBulkBuy = async () => {
    setBulkLoading(true);
    const status = {};
    for (const orderId of bulkSelected) {
      status[orderId] = 'pending';
      setBulkStatus({ ...status });
      try {
        await purchaseLabel(orderId, bulkRate);
        status[orderId] = 'success';
        setBulkStatus({ ...status });
      } catch {
        status[orderId] = 'error';
        setBulkStatus({ ...status });
      }
    }
    setBulkLoading(false);
    setBulkSelected([]);
  };

  // Bulk: handle select all
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setBulkSelected(ordersWithoutLabel.map(o => o.id));
      fetchBulkRates();
    } else {
      setBulkSelected([]);
      setBulkRatesList([]);
      setBulkRate('');
    }
  };

  // Bulk: handle individual select
  const handleSelectOrder = (orderId, checked) => {
    let newSelected = checked
      ? [...bulkSelected, orderId]
      : bulkSelected.filter(id => id !== orderId);
    setBulkSelected(newSelected);
    if (newSelected.length > 0) {
      fetchBulkRates();
    } else {
      setBulkRatesList([]);
      setBulkRate('');
    }
  };

  // Fetch products
  const fetchProducts = async () => {
    setProductLoading(true);
    setProductError('');
    try {
      const res = await adminFetch(`${API_BASE}/api/admin/products`, {
        credentials: 'include',
        headers: {
        }
      });
      
      if (!res.ok) {
        // Handle HTTP errors
        if (res.status === 401 || res.status === 403) {
          setProductError('Authentication failed. Please log in again.');
          handleAdminAuthFailure();
        } else {
          const errorData = await res.json().catch(() => ({ error: 'Unknown error' }));
          setProductError(errorData.error || `Failed to fetch products (${res.status})`);
        }
        setProductLoading(false);
        return;
      }
      
      const data = await res.json();
      if (data.success) {
        // Normalize images field for all products (never lose images; use main image if images empty)
        const normalized = data.products.map(p => {
          let images = p.images;
          if (typeof images === 'string') {
            try {
              images = JSON.parse(images);
            } catch {
              images = images ? [images] : [];
            }
          }
          if (!Array.isArray(images)) images = images ? [images] : [];
          if (images.length === 0 && p.image) images = [p.image];
          return { ...p, images };
        });
        setProducts(normalized);
        setProductError('');
      } else {
        setProductError(data.error || 'Failed to fetch products');
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setProductError('Failed to fetch products. Please check your connection.');
    }
    setProductLoading(false);
  };

  useEffect(() => { if (authed) fetchProducts(); }, [authed]);

  // Handle product form open/close
  const openAddProduct = () => {
    setEditingProduct(null);
    setProductForm({ name: '', slug: '', price: '', description: '', admin_notes: '', image: '', images: [], category_ids: [], show_on_commission_page: false, show_on_market_page: false });
    setShowProductForm(true);
  };
  const openEditProduct = (product) => {
    setEditingProduct(product);
    
    // Prepare images array - same logic as before but with debug logging
    const productImages = (product.images && product.images.length > 0)
      ? product.images
      : (product.image ? [product.image] : []);
    
    // Debug logging for SHANCE
    if (product.name && product.name.toLowerCase().includes('shance')) {
      console.log('🔍 Opening SHANCE edit form:', {
        productName: product.name,
        rawImages: product.images,
        rawImage: product.image,
        processedImages: productImages,
        imagesLength: productImages.length,
        imagesType: typeof product.images,
        imagesIsArray: Array.isArray(product.images)
      });
    }
    
    setProductForm({
      name: product.name,
      slug: product.slug || '',
      price: product.price,
      description: product.description,
      admin_notes: product.admin_notes || '',
      image: product.image || '',
      images: productImages,
      // Use product.categories if present, otherwise fallback to category_ids
      // Convert all IDs to strings for consistency
      category_ids: product.categories 
        ? product.categories.map(c => String(c.id || c._id))
        : (product.category_ids ? product.category_ids.map(id => String(id)) : []),
      show_on_commission_page: product.show_on_commission_page === true || product.show_on_commission_page === 'true',
      show_on_market_page: product.show_on_market_page === true || product.show_on_market_page === 'true'
    });
    setShowProductForm(true);
  };
  const closeProductForm = () => {
    setShowProductForm(false);
    setEditingProduct(null);
    setProductForm({ name: '', slug: '', price: '', description: '', admin_notes: '', image: '', images: [], category_ids: [], show_on_commission_page: false, show_on_market_page: false });
  };

  // Utility to strip background-color styles from HTML
  function stripBackgroundColor(html) {
    if (!html) return html;
    // Remove inline background-color styles (style="...background-color:...;")
    return html.replace(/background-color\s*:[^;"']+;?/gi, '');
  }

  // Handle product form submit
  const handleProductFormSubmit = async (e) => {
    e.preventDefault();
    if (!productForm.name || !productForm.price || !productForm.slug) return;
    setProductLoading(true);
    try {
      let res, data;
      // Convert editorState to HTML and update productForm.description
      const descriptionHtml = draftToHtml(convertToRaw(editorState.getCurrentContent()));
      // Convert category_ids to categoryIds for backend compatibility
      const { category_ids, ...restForm } = productForm;
      const categoryIds = Array.isArray(category_ids) ? category_ids : [];
      // Ensure images array is properly set before sanitizing
      let formImages = productForm.images;
      
      // Normalize images to always be an array
      if (!formImages) {
        formImages = [];
      } else if (!Array.isArray(formImages)) {
        formImages = [formImages];
      }
      
      // If editing and no images in form, preserve existing images from product
      if (editingProduct && formImages.length === 0) {
        // Try to get images from editingProduct
        if (editingProduct.images && Array.isArray(editingProduct.images) && editingProduct.images.length > 0) {
          formImages = editingProduct.images;
        } else if (editingProduct.image) {
          formImages = [editingProduct.image];
        }
        console.log('⚠️  No images in form state, preserving existing images from product:', formImages);
      }
      
      const sanitizedForm = { 
        ...restForm, 
        description: sanitizeProductDescription(stripBackgroundColor(descriptionHtml)),
        categoryIds: categoryIds, // Convert to camelCase for backend
        images: formImages, // Explicitly include images array (always an array)
        show_on_commission_page: productForm.show_on_commission_page || false,
        show_on_market_page: productForm.show_on_market_page || false
      };
      
      console.log('Saving product with categories:', categoryIds);
      console.log('Product form images:', sanitizedForm.images);
      console.log('Product form state:', productForm);
      console.log('show_on_commission_page in productForm:', productForm.show_on_commission_page);
      console.log('show_on_commission_page in sanitizedForm:', sanitizedForm.show_on_commission_page);
      if (editingProduct) {
        res = await adminFetch(`${API_BASE}/api/admin/products/${editingProduct.id}`, {
        credentials: 'include',
        method: 'PUT',
          headers: { 
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(sanitizedForm)
        });
        data = await res.json();
      } else {
        res = await adminFetch(`${API_BASE}/api/admin/products`, {
        credentials: 'include',
        method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(sanitizedForm)
        });
        data = await res.json();
      }
      if (data.success) {
        fetchProducts();
        closeProductForm();
      } else {
        setProductError(data.error || 'Failed to save product');
      }
    } catch {
      setProductError('Failed to save product');
    }
    setProductLoading(false);
  };

  // Handle product delete
  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    setProductLoading(true);
    try {
      const res = await adminFetch(`${API_BASE}/api/admin/products/${id}`, { 
        method: 'DELETE',
        headers: {
        }
      });
      const data = await res.json();
      if (data.success) {
        fetchProducts();
      } else {
        setProductError('Failed to delete product');
      }
    } catch {
      setProductError('Failed to delete product');
    }
    setProductLoading(false);
  };

  // Open size manager for a product
  const openSizeManager = (product) => {
    setEditingProductSizes(product);
    setShowSizeManager(true);
  };

  // Close size manager
  const closeSizeManager = () => {
    setShowSizeManager(false);
    setEditingProductSizes(null);
    setSizeForm({ size: '', quantity: 0, preorder: false, made_to_order: false });
    setSizeError('');
  };

  // Add a new size to a product
  const handleAddSize = async (e) => {
    e.preventDefault();
    if (!sizeForm.size || sizeForm.quantity < 0) return;
    
    if (!authed) {
      setSizeError('Not authenticated. Please log in again.');
      return;
    }
    setSizeLoading(true);
    try {
      const res = await adminFetch(`${API_BASE}/api/products/${editingProductSizes.id}/sizes`, {
        credentials: 'include',
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          
        },
        body: JSON.stringify(sizeForm)
      });
      const data = await res.json();
      if (data.success) {
        // Update local state immediately for better UX
        const newSize = {
          size: sizeForm.size,
          quantity: sizeForm.quantity,
          preorder: sizeForm.preorder || false,
          made_to_order: sizeForm.made_to_order || false
        };
        
        // Update products list
        setProducts(prevProducts => 
          prevProducts.map(product => {
            if (product.id === editingProductSizes.id) {
              const updatedSizes = [...(product.sizes || []), newSize];
              const total_inventory = updatedSizes.reduce((sum, s) => sum + s.quantity, 0);
              return { ...product, sizes: updatedSizes, total_inventory };
            }
            return product;
          })
        );
        
        // Update editingProductSizes state
        setEditingProductSizes(prev => {
          if (prev && prev.id === editingProductSizes.id) {
            const updatedSizes = [...(prev.sizes || []), newSize];
            const total_inventory = updatedSizes.reduce((sum, s) => sum + s.quantity, 0);
            return { ...prev, sizes: updatedSizes, total_inventory };
          }
          return prev;
        });
        
        setSizeForm({ size: '', quantity: 0, preorder: false, made_to_order: false });
        setSizeError('');
      } else {
        setSizeError(data.error || 'Failed to add size');
      }
    } catch {
      setSizeError('Failed to add size');
    }
    setSizeLoading(false);
  };

  // Add multiple sizes at once (for quick actions)
  const handleAddMultipleSizes = async (sizes) => {
    if (!authed) {
      setSizeError('Not authenticated. Please log in again.');
      return;
    }
    setSizeLoading(true);
    try {
      const addedSizes = [];
      for (const sizeData of sizes) {
        const res = await adminFetch(`${API_BASE}/api/products/${editingProductSizes.id}/sizes`, {
        credentials: 'include',
        method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            
          },
          body: JSON.stringify(sizeData)
        });
        const data = await res.json();
        if (data.success) {
          addedSizes.push(sizeData);
        } else {
          console.error(`Failed to add size ${sizeData.size}:`, data.error);
        }
      }
      
      if (addedSizes.length > 0) {
        // Update local state immediately for better UX
        
        // Update products list
        setProducts(prevProducts => 
          prevProducts.map(product => {
            if (product.id === editingProductSizes.id) {
              const updatedSizes = [...(product.sizes || []), ...addedSizes];
              const total_inventory = updatedSizes.reduce((sum, s) => sum + s.quantity, 0);
              return { ...product, sizes: updatedSizes, total_inventory };
            }
            return product;
          })
        );
        
        // Update editingProductSizes state
        setEditingProductSizes(prev => {
          if (prev && prev.id === editingProductSizes.id) {
            const updatedSizes = [...(prev.sizes || []), ...addedSizes];
            const total_inventory = updatedSizes.reduce((sum, s) => sum + s.quantity, 0);
            return { ...prev, sizes: updatedSizes, total_inventory };
          }
          return prev;
        });
      }
      
      setSizeError('');
    } catch (error) {
      setSizeError('Failed to add some sizes');
    }
    setSizeLoading(false);
  };

  // Update preorder status for a size
  const handleUpdatePreorder = async (productId, size, isPreorder) => {
    // Update state immediately for better UX (optimistic update)
    // Update products list
    setProducts(prevProducts => 
      prevProducts.map(product => {
        if (product.id === productId) {
          const updatedSizes = product.sizes.map(s => 
            s.size === size ? { ...s, preorder: isPreorder } : s
          );
          return { ...product, sizes: updatedSizes };
        }
        return product;
      })
    );
    // Also update the editingProductSizes state if it's the same product
    if (editingProductSizes && editingProductSizes.id === productId) {
      setEditingProductSizes(prev => {
        if (prev) {
          const updatedSizes = prev.sizes.map(s => 
            s.size === size ? { ...s, preorder: isPreorder } : s
          );
          return { ...prev, sizes: updatedSizes };
        }
        return prev;
      });
    }
    
    // Then make the API call - get current quantity to send with request
    try {
      // Find current size data to get quantity
      const currentSize = editingProductSizes?.sizes?.find(s => s.size === size);
      const currentQuantity = currentSize?.quantity ?? 0;
      
      if (!authed) {
      console.error('Not authenticated. Please log in again.');
        return;
    }
    const res = await adminFetch(`${API_BASE}/api/products/${productId}/sizes/${size}/inventory`, {
        credentials: 'include',
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          
        },
        body: JSON.stringify({ 
          preorder: isPreorder,
          quantity: currentQuantity  // Include current quantity to preserve it
        })
      });
      const data = await res.json();
      if (!data.success) {
        // If API call fails, revert the state change
        console.error('Failed to update preorder status');
        // Revert to previous state
        setProducts(prevProducts => 
          prevProducts.map(product => {
            if (product.id === productId) {
              const updatedSizes = product.sizes.map(s => 
                s.size === size ? { ...s, preorder: !isPreorder } : s
              );
              return { ...product, sizes: updatedSizes };
            }
            return product;
          })
        );
        if (editingProductSizes && editingProductSizes.id === productId) {
          setEditingProductSizes(prev => {
            if (prev) {
              const updatedSizes = prev.sizes.map(s => 
                s.size === size ? { ...s, preorder: !isPreorder } : s
              );
              return { ...prev, sizes: updatedSizes };
            }
            return prev;
          });
        }
      }
    } catch (error) {
      console.error('Failed to update preorder status:', error);
      // Revert to previous state on error
      setProducts(prevProducts => 
        prevProducts.map(product => {
          if (product.id === productId) {
            const updatedSizes = product.sizes.map(s => 
              s.size === size ? { ...s, preorder: !isPreorder } : s
            );
            return { ...product, sizes: updatedSizes };
          }
          return product;
        })
      );
      if (editingProductSizes && editingProductSizes.id === productId) {
        setEditingProductSizes(prev => {
          if (prev) {
            const updatedSizes = prev.sizes.map(s => 
              s.size === size ? { ...s, preorder: !isPreorder } : s
            );
            return { ...prev, sizes: updatedSizes };
          }
          return prev;
        });
      }
    }
  };

  // Update made_to_order status for a size
  const handleUpdateMadeToOrder = async (productId, size, isMadeToOrder) => {
    // Optimistic update
    setProducts(prevProducts =>
      prevProducts.map(product => {
        if (product.id === productId) {
          const updatedSizes = product.sizes.map(s =>
            s.size === size ? { ...s, made_to_order: isMadeToOrder } : s
          );
          return { ...product, sizes: updatedSizes };
        }
        return product;
      })
    );

    if (editingProductSizes && editingProductSizes.id === productId) {
      setEditingProductSizes(prev => {
        if (prev) {
          const updatedSizes = prev.sizes.map(s =>
            s.size === size ? { ...s, made_to_order: isMadeToOrder } : s
          );
          return { ...prev, sizes: updatedSizes };
        }
        return prev;
      });
    }

    try {
      const currentSize = editingProductSizes?.sizes?.find(s => s.size === size);
      const currentQuantity = currentSize?.quantity ?? 0;

      if (!authed) {
      console.error('Not authenticated. Please log in again.');
        return;
    }
    const res = await adminFetch(`${API_BASE}/api/products/${productId}/sizes/${size}/inventory`, {
        credentials: 'include',
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          
        },
        body: JSON.stringify({
          made_to_order: isMadeToOrder,
          quantity: currentQuantity
        })
      });
      const data = await res.json();
      if (!data.success) {
        console.error('Failed to update made-to-order status');
        setProducts(prevProducts =>
          prevProducts.map(product => {
            if (product.id === productId) {
              const updatedSizes = product.sizes.map(s =>
                s.size === size ? { ...s, made_to_order: !isMadeToOrder } : s
              );
              return { ...product, sizes: updatedSizes };
            }
            return product;
          })
        );
        if (editingProductSizes && editingProductSizes.id === productId) {
          setEditingProductSizes(prev => {
            if (prev) {
              const updatedSizes = prev.sizes.map(s =>
                s.size === size ? { ...s, made_to_order: !isMadeToOrder } : s
              );
              return { ...prev, sizes: updatedSizes };
            }
            return prev;
          });
        }
      }
    } catch (error) {
      console.error('Failed to update made-to-order status:', error);
      setProducts(prevProducts =>
        prevProducts.map(product => {
          if (product.id === productId) {
            const updatedSizes = product.sizes.map(s =>
              s.size === size ? { ...s, made_to_order: !isMadeToOrder } : s
            );
            return { ...product, sizes: updatedSizes };
          }
          return product;
        })
      );
      if (editingProductSizes && editingProductSizes.id === productId) {
        setEditingProductSizes(prev => {
          if (prev) {
            const updatedSizes = prev.sizes.map(s =>
              s.size === size ? { ...s, made_to_order: !isMadeToOrder } : s
            );
            return { ...prev, sizes: updatedSizes };
          }
          return prev;
        });
      }
    }
  };

  // Update inventory quantity for a size
  const handleUpdateInventory = async (productId, size, newQuantity) => {
    if (newQuantity < 0) return;
    
    if (!authed) {
      console.error('Not authenticated. Please log in again.');
      return;
    }
    try {
      const res = await adminFetch(`${API_BASE}/api/products/${productId}/sizes/${size}/inventory`, {
        credentials: 'include',
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          
        },
        body: JSON.stringify({ quantity: newQuantity })
      });
      const data = await res.json();
      if (data.success) {
        // Update the local state immediately for better UX
        setProducts(prevProducts => 
          prevProducts.map(product => {
            if (product.id === productId) {
              const updatedSizes = product.sizes.map(s => 
                s.size === size ? { ...s, quantity: newQuantity } : s
              );
              const total_inventory = updatedSizes.reduce((sum, s) => sum + s.quantity, 0);
              return { ...product, sizes: updatedSizes, total_inventory };
            }
            return product;
          })
        );
        
        // Also update the editingProductSizes state if it's the same product
        if (editingProductSizes && editingProductSizes.id === productId) {
          setEditingProductSizes(prev => {
            if (prev && prev.id === productId) {
              const updatedSizes = prev.sizes.map(s => 
                s.size === size ? { ...s, quantity: newQuantity } : s
              );
              const total_inventory = updatedSizes.reduce((sum, s) => sum + s.quantity, 0);
              return { ...prev, sizes: updatedSizes, total_inventory };
            }
            return prev;
          });
        }
      }
    } catch {
      console.error('Failed to update inventory');
    }
  };

  // Delete a size from a product
  const handleDeleteSize = async (productId, size) => {
    if (!window.confirm(`Delete size ${size}?`)) return;
    
    if (!authed) {
      console.error('Not authenticated. Please log in again.');
      return;
    }
    try {
      const res = await adminFetch(`${API_BASE}/api/products/${productId}/sizes/${size}`, {
        credentials: 'include',
        method: 'DELETE',
        headers: {
          
        }
      });
      const data = await res.json();
      if (data.success) {
        // Update local state immediately for better UX
        
        // Update products list
        setProducts(prevProducts => 
          prevProducts.map(product => {
            if (product.id === productId) {
              const updatedSizes = product.sizes.filter(s => s.size !== size);
              const total_inventory = updatedSizes.reduce((sum, s) => sum + s.quantity, 0);
              return { ...product, sizes: updatedSizes, total_inventory };
            }
            return product;
          })
        );
        
        // Update editingProductSizes state if it's the same product
        if (editingProductSizes && editingProductSizes.id === productId) {
          setEditingProductSizes(prev => {
            if (prev && prev.id === productId) {
              const updatedSizes = prev.sizes.filter(s => s.size !== size);
              const total_inventory = updatedSizes.reduce((sum, s) => sum + s.quantity, 0);
              return { ...prev, sizes: updatedSizes, total_inventory };
            }
            return prev;
          });
        }
      }
    } catch {
      console.error('Failed to delete size');
    }
  };

  // Bulk update inventory for all sizes of a product
  const handleBulkInventoryUpdate = async (newSizes) => {
    if (!editingProductSizes?.id) return;
    
    // Show confirmation for bulk operations
    const totalChange = newSizes.reduce((sum, s) => {
      const oldSize = editingProductSizes.sizes.find(old => old.size === s.size);
      return sum + Math.abs((s.quantity || 0) - (oldSize?.quantity || 0));
    }, 0);
    
    if (totalChange > 50) {
      const confirmed = window.confirm(
        `This will change inventory by ${totalChange} units across ${newSizes.length} sizes. Are you sure?`
      );
      if (!confirmed) return;
    }
    
    setSizeLoading(true);
    try {
      // Use the PUT endpoint to update all sizes at once
      const res = await adminFetch(`${API_BASE}/api/products/${editingProductSizes.id}/sizes`, {
        credentials: 'include',
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sizes: newSizes })
      });
      const data = await res.json();
      if (data.success) {
        // Update local state immediately for better UX
        
        // Update products list
        setProducts(prevProducts => 
          prevProducts.map(product => {
            if (product.id === editingProductSizes.id) {
              const total_inventory = newSizes.reduce((sum, s) => sum + s.quantity, 0);
              return { ...product, sizes: newSizes, total_inventory };
            }
            return product;
          })
        );
        
        // Update editingProductSizes state
        setEditingProductSizes(prev => {
          if (prev && prev.id === editingProductSizes.id) {
            const total_inventory = newSizes.reduce((sum, s) => sum + s.quantity, 0);
            return { ...prev, sizes: newSizes, total_inventory };
          }
          return prev;
        });
        
        setSizeError('');
      } else {
        setSizeError(data.error || 'Failed to update inventory');
      }
    } catch (error) {
      setSizeError('Failed to update inventory');
    }
    setSizeLoading(false);
  };

  const handleImageFiles = async (fileList) => {
    if (!fileList?.length) return;
    const allFiles = Array.from(fileList);
    const files = allFiles.filter(isImageFile);
    const rejected = allFiles.filter((f) => !isImageFile(f));
    if (files.length === 0) {
      setProductError(
        rejected.length
          ? `Could not upload: ${rejected.map(describeRejectedImage).join('; ')}`
          : 'Please drop or select JPEG, PNG, GIF, or WebP images'
      );
      return;
    }

    setProductLoading(true);
    setProductError(
      rejected.length
        ? `Skipping unsupported file(s): ${rejected.map(describeRejectedImage).join('; ')}. Uploading ${files.length}…`
        : `Uploading 0/${files.length}…`
    );

    const uploaded = [];
    const failures = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setProductError(
        rejected.length
          ? `Skipping unsupported file(s). Uploading ${i + 1}/${files.length}…`
          : `Uploading ${i + 1}/${files.length}…`
      );
      try {
        const url = await uploadAdminImage(file);
        uploaded.push(url);
        setProductForm((f) => ({ ...f, images: [...(f.images || []), url] }));
      } catch (err) {
        console.error('❌ Upload error:', err);
        const msg = err.message || 'Image upload failed';
        failures.push(`${file.name || 'image'}: ${msg}`);
        if (/no token|invalid or expired token|authentication/i.test(msg)) {
          handleAdminAuthFailure();
          break;
        }
      }
    }

    if (failures.length) {
      setProductError(
        `Uploaded ${uploaded.length} of ${files.length}. Failed: ${failures.slice(0, 3).join('; ')}${
          failures.length > 3 ? ` (+${failures.length - 3} more)` : ''
        }`
      );
    } else if (rejected.length) {
      setProductError(
        `Uploaded ${uploaded.length}. Skipped: ${rejected.map(describeRejectedImage).join('; ')}`
      );
    } else {
      setProductError('');
    }
    setProductLoading(false);
  };
  const handleImageDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Ignore empty drops (e.g. leftover HTML5 reorder gestures)
    if (!e.dataTransfer?.files?.length) return;
    handleImageFiles(e.dataTransfer.files);
  };
  const handleImageDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.dataTransfer) return;
    // Only advertise copy for real OS file drags — not thumbnail reorders
    if (dataTransferHasFiles(e.dataTransfer) && dragImageIdx.current === null) {
      e.dataTransfer.dropEffect = 'copy';
    } else {
      e.dataTransfer.dropEffect = 'move';
    }
  };
  const handleImageDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  // Remove image
  const handleRemoveImage = (idx) => {
    setProductForm(f => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));
  };
  // File input ref for "click to select" in product images
  const productImageInputRef = useRef(null);
  // Drag to reorder images
  const dragImageIdx = useRef(null);
  const handleImageDragStart = (idx) => { dragImageIdx.current = idx; };
  const handleImageDropReorder = (idx) => {
    if (dragImageIdx.current === null) return;
    const from = dragImageIdx.current;
    dragImageIdx.current = null;
    if (from === idx) return;
    setProductForm(f => {
      const imgs = [...(f.images || [])];
      const [dragged] = imgs.splice(from, 1);
      imgs.splice(idx, 0, dragged);
      return { ...f, images: imgs };
    });
  };
  // Set main image
  const handleSetMainImage = (idx) => {
    setProductForm(f => {
      const imgs = [...(f.images || [])];
      const [main] = imgs.splice(idx, 1);
      imgs.unshift(main);
      return { ...f, images: imgs };
    });
  };

  // Add this function to handle order deletion
  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order?')) return;
    try {
      const res = await adminFetch(`${API_BASE}/api/admin/orders/${orderId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json();
      if (data.success) {
        setOrders(prev => prev.filter(o => o.id !== orderId));
      } else {
        alert('Failed to delete order.');
      }
    } catch (err) {
      alert('Failed to delete order.');
    }
  };

  if (!authed) {
    return (
      <div className="admin-page admin-page--login">
        <div className="admin-login">
          <div className="admin-brand">Bokeh<span className="admin-brand-sub">Administration</span></div>
          <div className="admin-login-label">Secure Access</div>
          <form onSubmit={handleLogin}>
            <div className="admin-form-row">
              <label htmlFor="admin-password">Password</label>
              <input
                id="admin-password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Enter admin password"
                style={{ width: '100%', padding: '10px 12px', boxSizing: 'border-box' }}
              />
            </div>
            <button type="submit" className="admin-btn" style={{ width: '100%', padding: '12px 18px', marginTop: 8 }}>
              Enter
            </button>
            {error && <div className="admin-login-error">{error}</div>}
          </form>
        </div>
      </div>
    );
  }

  const getUrl = img =>
    img && img.startsWith('/uploads/')
      ? `${UPLOADS_BASE}${img}`
      : img && !img.startsWith('http')
        ? `${UPLOADS_BASE}/uploads/${img.replace(/^\/+/, '')}`
        : img;

  // Fetch Pinterest boards
  const fetchPinterestBoards = async () => {
    setPinterestLoading(true);
    setPinterestError('');
    try {
      const response = await adminFetch(`${API_BASE}/api/admin/pinterest/boards`, {
        credentials: 'include',
        headers: {
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch boards');
      }
      
      const data = await response.json();
      setPinterestBoards(data.boards || []);
      
      if (data.boards && data.boards.length > 0) {
        alert(`✅ Found ${data.boards.length} boards! Check the dropdown below to select one.`);
      } else {
        alert('⚠️ No boards found. You may need to create a board on Pinterest first.');
      }
    } catch (error) {
      console.error('Failed to fetch boards:', error);
      setPinterestError(error.message || 'Failed to fetch boards');
      alert(`❌ Error: ${error.message || 'Failed to fetch boards'}`);
    } finally {
      setPinterestLoading(false);
    }
  };

  const handleDownloadDatabase = async () => {
    try {
      if (!authed) {
        alert('You need to log in again. Your session may have expired.');
        return;
      }

      const response = await adminFetch(`${API_BASE}/api/admin/download-db`, {
        method: 'GET',
        credentials: 'include',
      });
      if (!response.ok) {
        const errorData = await response.text();
        console.error('Error response:', errorData);
        throw new Error(`Failed to download database: ${response.status} - ${errorData}`);
      }

      // Create a blob from the response
      const blob = await response.blob();
      console.log('Blob size:', blob.size, 'bytes');
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ecommerce.db';
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      alert('✅ Database downloaded! Save this to ecommerce-clothing/backend/ecommerce.db, then commit and push.');
    } catch (error) {
      console.error('Download error:', error);
      alert(`❌ Failed to download database: ${error.message}`);
    }
  };

  const activeSection = ADMIN_SECTIONS.find(s => s.id === adminTab) || ADMIN_SECTIONS[0];

  return (
    <>
    <div className="admin-page">
      <div className="admin-shell">
        <aside className="admin-sidebar">
          <div className="admin-brand">
            Bokeh
            <span className="admin-brand-sub">Store Admin</span>
          </div>
          <nav className="admin-nav">
            {ADMIN_SECTIONS.map(section => (
              <button
                key={section.id}
                type="button"
                className={`admin-nav-item${adminTab === section.id ? ' admin-nav-item--active' : ''}`}
                onClick={() => setAdminTab(section.id)}
              >
                <span className="admin-nav-num">{section.num}.</span>
                {section.label}
              </button>
            ))}
          </nav>
          <div className="admin-sidebar-footer">
            <button type="button" className="admin-btn admin-btn--ghost" onClick={handleDownloadDatabase} style={{ width: '100%' }}>
              Download Database
            </button>
            <button type="button" className="admin-btn" onClick={handleLogout} style={{ width: '100%' }}>
              Logout
            </button>
          </div>
        </aside>

        <main className="admin-main">
          <header className="admin-main-header">
            <h1 className="admin-main-title">{activeSection.label}</h1>
            <p className="admin-main-desc">{activeSection.desc}</p>
          </header>
          <div className="admin-main-content">
      
      {/* Admin Tab Content */}
      {/* Orders Management Section (tab) */}
      {adminTab === 'orders' && (
        <div className="admin-section">
          {loading && <div className="admin-message admin-message--loading">Loading orders…</div>}
          {fetchError && <div className="admin-message admin-message--error">{fetchError}</div>}
          {!loading && !fetchError && sortedOrders.length === 0 && <div className="admin-message">No orders found.</div>}
          {!loading && !fetchError && sortedOrders.length > 0 && (
            <>
              <div className="admin-toolbar" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
                <label style={{ fontWeight: 'bold', marginRight: 12 }}>
                  <input
                    type="checkbox"
                    checked={bulkSelected.length === ordersWithoutLabel.length && ordersWithoutLabel.length > 0}
                    onChange={handleSelectAll}
                    disabled={ordersWithoutLabel.length === 0 || bulkLoading}
                  />
                  {' '}Select All Orders Without Label
                </label>
                {trackingSyncing && (
                  <span style={{ fontSize: 13, color: '#666' }}>Updating tracking statuses…</span>
                )}
                {bulkSelected.length > 0 && (
                  <>
                    <select
                      value={bulkRate}
                      onChange={e => setBulkRate(e.target.value)}
                      style={{ marginLeft: 16, padding: 6, fontSize: 15 }}
                      disabled={bulkLoading || bulkRatesList.length === 0}
                    >
                      {bulkRatesList.length === 0 && <option value=''>No common rates</option>}
                      {bulkRatesList.map(rate => (
                        <option key={rate.object_id} value={rate.object_id}>
                          {rate.provider} {rate.servicelevel?.name} - ${rate.amount} ({rate.estimated_days || '?'} days)
                        </option>
                      ))}
                    </select>
                    <button
                      style={{ marginLeft: 12 }}
                      disabled={bulkLoading || !bulkRate}
                      onClick={handleBulkBuy}
                    >
                      {bulkLoading ? 'Purchasing Labels...' : 'Buy Labels for Selected'}
                    </button>
                  </>
                )}
              </div>
              {activeOrders.length === 0 ? (
                <div className="admin-message">No open orders. Delivered orders are in Old Orders below.</div>
              ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16 }}>
                <thead>
                  <tr style={{ background: '#f5f5f5' }}>
                    <th style={{ padding: 8, border: '1px solid #ddd' }}></th>
                    <th style={{ padding: 8, border: '1px solid #ddd' }}>Order ID</th>
                    <th style={{ padding: 8, border: '1px solid #ddd' }}>Customer</th>
                    <th style={{ padding: 8, border: '1px solid #ddd' }}>Email</th>
                    <th style={{ padding: 8, border: '1px solid #ddd' }}>Address</th>
                    <th style={{ padding: 8, border: '1px solid #ddd' }}>Items</th>
                    <th style={{ padding: 8, border: '1px solid #ddd' }}>Total</th>
                    <th style={{ padding: 8, border: '1px solid #ddd' }}>Status</th>
                    <th style={{ padding: 8, border: '1px solid #ddd' }}>Created</th>
                    <th style={{ padding: 8, border: '1px solid #ddd' }}>Fulfillment</th>
                    <th style={{ padding: 8, border: '1px solid #ddd' }}>Shippo</th>
                  </tr>
                </thead>
                <tbody>
                  {activeOrders.map(order => (
                    <tr key={order.id}>
                      <td style={{ padding: 8, border: '1px solid #ddd', textAlign: 'center' }}>
                        {!order.is_market_order && !labelLinks[order.id] && !order.label_url && (
                          <input
                            type="checkbox"
                            checked={bulkSelected.includes(order.id)}
                            onChange={e => handleSelectOrder(order.id, e.target.checked)}
                            disabled={bulkLoading}
                          />
                        )}
                      </td>
                      <td style={{ padding: 8, border: '1px solid #ddd' }}>{order.id}</td>
                      <td style={{ padding: 8, border: '1px solid #ddd' }}>{order.customer_name}</td>
                      <td style={{ padding: 8, border: '1px solid #ddd' }}>{order.customer_email}</td>
                      <td style={{ padding: 8, border: '1px solid #ddd' }}>{order.shipping_address}, {order.shipping_city}, {order.shipping_state} {order.shipping_zip}, {order.shipping_country}</td>
                      <td style={{ padding: 8, border: '1px solid #ddd' }}>
                        {Array.isArray(order.items) && order.items.length > 0
                          ? order.items.map(formatOrderItem).join('; ')
                          : '—'}
                      </td>
                      <td style={{ padding: 8, border: '1px solid #ddd' }}>${Number(order.total || 0).toFixed(2)}</td>
                      <td style={{ padding: 8, border: '1px solid #ddd' }}>{order.status}</td>
                      <td style={{ padding: 8, border: '1px solid #ddd' }}>{order.created_at ? new Date(order.created_at).toLocaleString() : ''}</td>
                      <td style={{ padding: 8, border: '1px solid #ddd', textAlign: 'center' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center' }}>
                          <FulfillmentBadge stage={getOrderFulfillmentStage(order, labelLinks)} />
                          {order.is_market_order && (
                            <span style={{ fontSize: 11, color: '#666' }}>Market</span>
                          )}
                          {bulkStatus[order.id] === 'pending' && <span style={{ color: '#888', fontSize: 12 }}>Purchasing...</span>}
                          {bulkStatus[order.id] === 'error' && <span style={{ color: 'red', fontSize: 12 }}>Label error</span>}
                        </div>
                      </td>
                      <td style={{ padding: 8, border: '1px solid #ddd' }}>
                        {order.is_market_order ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 200 }}>
                            <div>
                              <label style={{ display: 'block', fontSize: 11, marginBottom: 2 }}>Tracking #</label>
                              <input
                                type="text"
                                value={marketOrderForm[order.id]?.tracking_number ?? order.tracking_number ?? ''}
                                onChange={e => setMarketOrderForm(prev => ({ ...prev, [order.id]: { ...(prev[order.id] || {}), tracking_number: e.target.value } }))}
                                placeholder="Enter tracking number"
                                style={{ width: '100%', padding: 6, fontSize: 13 }}
                              />
                            </div>
                            <div>
                              <label style={{ display: 'block', fontSize: 11, marginBottom: 2 }}>Carrier (e.g. USPS, UPS)</label>
                              <input
                                type="text"
                                value={marketOrderForm[order.id]?.carrier ?? order.carrier ?? ''}
                                onChange={e => setMarketOrderForm(prev => ({ ...prev, [order.id]: { ...(prev[order.id] || {}), carrier: e.target.value } }))}
                                placeholder="USPS, UPS, FedEx"
                                style={{ width: '100%', padding: 6, fontSize: 13 }}
                              />
                            </div>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                              <input
                                type="checkbox"
                                checked={marketOrderForm[order.id]?.product_purchased_for_customer ?? order.product_purchased_for_customer ?? false}
                                onChange={e => setMarketOrderForm(prev => ({ ...prev, [order.id]: { ...(prev[order.id] || {}), product_purchased_for_customer: e.target.checked } }))}
                              />
                              Purchased to ship to customer
                            </label>
                            <button
                              disabled={marketOrderSaving[order.id]}
                              onClick={() => saveMarketOrderTracking(order.id)}
                              style={{ background: '#000', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 12px', fontWeight: 'bold', cursor: 'pointer', alignSelf: 'flex-start' }}
                            >
                              {marketOrderSaving[order.id] ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              style={{ background: '#b00', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 12px', fontWeight: 'bold', cursor: 'pointer' }}
                              onClick={() => handleDeleteOrder(order.id)}
                            >
                              Delete order
                            </button>
                          </div>
                        ) : (
                          <>
                            {order.label_url ? (
                              <div>
                                <a href={order.label_url} target="_blank" rel="noopener noreferrer">Download Label</a><br/>
                                <span style={{ fontSize: 12 }}>Tracking: {order.tracking_number || '—'}</span>
                              </div>
                            ) : labelLinks[order.id] ? (
                              <div>
                                <a href={labelLinks[order.id].url} target="_blank" rel="noopener noreferrer">Download Label</a><br/>
                                <span style={{ fontSize: 12 }}>Tracking: {labelLinks[order.id].tracking}</span>
                              </div>
                            ) : rates[order.id] ? (
                              <div>
                                {rates[order.id].length === 0 && <span>No rates found.</span>}
                                {rates[order.id].length > 0 && (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexDirection: 'column' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                      <select
                                        value={selectedRate[order.id] || ''}
                                        onChange={e => setSelectedRate(r => ({ ...r, [order.id]: e.target.value }))}
                                        style={{ padding: 6, fontSize: 15 }}
                                      >
                                        <option value='' disabled>Select a rate</option>
                                        {rates[order.id].map(rate => (
                                          <option key={rate.object_id} value={rate.object_id}>
                                            {rate.provider} {rate.servicelevel?.name} - ${rate.amount} ({rate.estimated_days || '?'} days)
                                          </option>
                                        ))}
                                      </select>
                                      <button
                                        disabled={purchaseLoading[order.id] || !selectedRate[order.id]}
                                        onClick={() => purchaseLabel(order.id, selectedRate[order.id])}
                                        style={{ background: '#000', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', fontWeight: 'bold', cursor: 'pointer' }}
                                      >
                                        {purchaseLoading[order.id] ? 'Purchasing...' : 'Buy Label'}
                                      </button>
                                    </div>
                                    <button
                                      style={{ background: '#b00', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', fontWeight: 'bold', cursor: 'pointer', marginTop: 6 }}
                                      onClick={() => handleDeleteOrder(order.id)}
                                    >
                                      Delete
                                    </button>
                                  </div>
                                )}
                              </div>
                            ) : rateLoading[order.id] ? (
                              <span>Loading rates...</span>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
                                <button onClick={() => fetchRates(order)} disabled={rateLoading[order.id]}>View Rates</button>
                                <button
                                  style={{ background: '#b00', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', fontWeight: 'bold', cursor: 'pointer', marginTop: 6 }}
                                  onClick={() => handleDeleteOrder(order.id)}
                                >
                                  Delete
                                </button>
                              </div>
                            )}
                            {rateError[order.id] && <div style={{ color: 'red', fontSize: 12 }}>{rateError[order.id]}</div>}
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              )}

              <div style={{ marginTop: 28, borderTop: '1px solid #ddd', paddingTop: 16 }}>
                <button
                  type="button"
                  onClick={() => setOldOrdersOpen((open) => !open)}
                  style={{
                    background: '#fff',
                    border: '1px solid #ccc',
                    borderRadius: 6,
                    padding: '10px 14px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    width: '100%',
                    textAlign: 'left',
                  }}
                >
                  {oldOrdersOpen ? '▾' : '▸'} Old Orders ({deliveredOrders.length} delivered)
                </button>
                {oldOrdersOpen && (
                  <div style={{ marginTop: 12 }}>
                    {deliveredOrders.length === 0 ? (
                      <div className="admin-message">No delivered orders yet.</div>
                    ) : (
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 15 }}>
                        <thead>
                          <tr style={{ background: '#f5f5f5' }}>
                            <th style={{ padding: 8, border: '1px solid #ddd' }}>Order ID</th>
                            <th style={{ padding: 8, border: '1px solid #ddd' }}>Customer</th>
                            <th style={{ padding: 8, border: '1px solid #ddd' }}>Email</th>
                            <th style={{ padding: 8, border: '1px solid #ddd' }}>Items</th>
                            <th style={{ padding: 8, border: '1px solid #ddd' }}>Total</th>
                            <th style={{ padding: 8, border: '1px solid #ddd' }}>Delivered</th>
                            <th style={{ padding: 8, border: '1px solid #ddd' }}>Fulfillment</th>
                            <th style={{ padding: 8, border: '1px solid #ddd' }}>Tracking</th>
                          </tr>
                        </thead>
                        <tbody>
                          {deliveredOrders.map((order) => (
                            <tr key={order.id}>
                              <td style={{ padding: 8, border: '1px solid #ddd' }}>{order.id}</td>
                              <td style={{ padding: 8, border: '1px solid #ddd' }}>{order.customer_name}</td>
                              <td style={{ padding: 8, border: '1px solid #ddd' }}>{order.customer_email}</td>
                              <td style={{ padding: 8, border: '1px solid #ddd' }}>
                                {Array.isArray(order.items) && order.items.length > 0
                                  ? order.items.map(formatOrderItem).join('; ')
                                  : '—'}
                              </td>
                              <td style={{ padding: 8, border: '1px solid #ddd' }}>${Number(order.total || 0).toFixed(2)}</td>
                              <td style={{ padding: 8, border: '1px solid #ddd' }}>
                                {order.delivered_at
                                  ? new Date(order.delivered_at).toLocaleString()
                                  : (order.updated_at ? new Date(order.updated_at).toLocaleString() : '—')}
                              </td>
                              <td style={{ padding: 8, border: '1px solid #ddd', textAlign: 'center' }}>
                                <FulfillmentBadge stage="delivered" />
                              </td>
                              <td style={{ padding: 8, border: '1px solid #ddd' }}>
                                {order.tracking_url ? (
                                  <a href={order.tracking_url} target="_blank" rel="noopener noreferrer">
                                    {order.tracking_number || 'Track'}
                                  </a>
                                ) : (
                                  order.tracking_number || '—'
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
      {/* Categories Management Section (tab) */}
      {adminTab === 'categories' && (
        <div className="admin-section">
            <div className="admin-toolbar">
            <button 
              type="button"
              className="admin-btn"
              onClick={() => setShowCategoryForm(true)} 
            >
              Add Category
            </button>
            </div>
            {categoryLoading && <div className="admin-message admin-message--loading">Loading…</div>}
            {categoryError && <div className="admin-message admin-message--error">{categoryError}</div>}

            {showCategoryForm && (
              <div className="admin-panel">
                <h3 className="admin-panel-title">Add New Category</h3>
                <form onSubmit={addCategory}>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
                      Category Name:
                    </label>
                    <input
                      type="text"
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      style={{ width: '100%', padding: 8, fontSize: 16, borderRadius: 4, border: '1px solid #ccc' }}
                      placeholder="Enter category name"
                      required
                    />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                    <button
                      type="button"
                      onClick={() => {
                        setShowCategoryForm(false);
                        setNewCategoryName('');
                        setCategoryError('');
                      }}
                      style={{ padding: '8px 18px', borderRadius: 6, background: '#eee', color: '#333', border: 'none', fontWeight: 'bold', cursor: 'pointer' }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      style={{ padding: '8px 18px', borderRadius: 6, background: '#000', color: '#fff', border: 'none', fontWeight: 'bold', cursor: 'pointer' }}
                    >
                      Add Category
                    </button>
                  </div>
                </form>
              </div>
            )}

            {categoryLoading ? (
              <div style={{ textAlign: 'center', padding: 40 }}>Loading categories...</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16 }}>
                <thead>
                  <tr style={{ background: '#f5f5f5' }}>
                    <th style={{ padding: 12, border: '1px solid #ddd', textAlign: 'left' }}>ID</th>
                    <th style={{ padding: 12, border: '1px solid #ddd', textAlign: 'left' }}>Name</th>
                    <th style={{ padding: 12, border: '1px solid #ddd', textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.length === 0 ? (
                    <tr>
                      <td colSpan="3" style={{ padding: 20, textAlign: 'center', color: '#666' }}>
                        No categories yet. Add your first category above!
                      </td>
                    </tr>
                  ) : (
                    categories.map(category => (
                      <tr key={category.id || category._id}>
                        <td style={{ padding: 12, border: '1px solid #ddd' }}>{(category.id || category._id || '').toString().substring(0, 8)}...</td>
                        <td style={{ padding: 12, border: '1px solid #ddd' }}>{category.name}</td>
                        <td style={{ padding: 12, border: '1px solid #ddd', textAlign: 'center' }}>
                          <button
                            onClick={() => deleteCategory(category.id || category._id)}
                            style={{ padding: '6px 12px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 14 }}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
      )}
      {adminTab === 'discounts' && (
        <div className="admin-section">
          <div className="admin-toolbar">
            <button type="button" className="admin-btn" onClick={openCreateDiscountForm}>
              Create Discount Code
            </button>
          </div>
          {discountLoading && <div className="admin-message admin-message--loading">Loading…</div>}
          {discountError && <div className="admin-message admin-message--error">{discountError}</div>}

          {showDiscountForm && (
            <div className="admin-panel">
              <h3 className="admin-panel-title">{editingDiscount ? 'Edit Discount Code' : 'Create Discount Code'}</h3>
              <form onSubmit={saveDiscountCode}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>Code</label>
                    <input
                      type="text"
                      value={discountForm.code}
                      onChange={(e) => setDiscountForm({ ...discountForm, code: e.target.value.toUpperCase() })}
                      placeholder="SUMMER20"
                      style={{ width: '100%', padding: 8, fontSize: 16, borderRadius: 4, border: '1px solid #ccc', boxSizing: 'border-box' }}
                      required
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>Discount type</label>
                    <select
                      value={discountForm.type}
                      onChange={(e) => setDiscountForm({ ...discountForm, type: e.target.value })}
                      style={{ width: '100%', padding: 8, fontSize: 16, borderRadius: 4, border: '1px solid #ccc', boxSizing: 'border-box', background: '#fff' }}
                    >
                      <option value="percent">Percentage off</option>
                      <option value="amount">Fixed amount off ($)</option>
                    </select>
                  </div>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
                    {discountForm.type === 'percent' ? 'Percentage off' : 'Amount off ($)'}
                  </label>
                  <input
                    type="number"
                    min="0.01"
                    step={discountForm.type === 'percent' ? '1' : '0.01'}
                    max={discountForm.type === 'percent' ? '100' : undefined}
                    value={discountForm.value}
                    onChange={(e) => setDiscountForm({ ...discountForm, value: e.target.value })}
                    style={{ width: '100%', maxWidth: 240, padding: 8, fontSize: 16, borderRadius: 4, border: '1px solid #ccc' }}
                    required
                  />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>When should this code stop working?</label>
                  <select
                    value={discountForm.limitMode}
                    onChange={(e) => setDiscountForm({ ...discountForm, limitMode: e.target.value })}
                    style={{ width: '100%', maxWidth: 420, padding: 8, fontSize: 16, borderRadius: 4, border: '1px solid #ccc', background: '#fff' }}
                  >
                    <option value="none">No limit (until you disable or delete it)</option>
                    <option value="date">Expires on a date</option>
                    <option value="uses">After a number of uses</option>
                    <option value="both">Both date and use limit (whichever comes first)</option>
                  </select>
                </div>

                {(discountForm.limitMode === 'date' || discountForm.limitMode === 'both') && (
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>Expiration date</label>
                    <input
                      type="datetime-local"
                      value={discountForm.expires_at}
                      onChange={(e) => setDiscountForm({ ...discountForm, expires_at: e.target.value })}
                      style={{ width: '100%', maxWidth: 320, padding: 8, fontSize: 16, borderRadius: 4, border: '1px solid #ccc' }}
                      required
                    />
                  </div>
                )}

                {(discountForm.limitMode === 'uses' || discountForm.limitMode === 'both') && (
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>Maximum uses</label>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={discountForm.max_uses}
                      onChange={(e) => setDiscountForm({ ...discountForm, max_uses: e.target.value })}
                      placeholder="e.g. 100"
                      style={{ width: '100%', maxWidth: 240, padding: 8, fontSize: 16, borderRadius: 4, border: '1px solid #ccc' }}
                      required
                    />
                  </div>
                )}

                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 'bold' }}>
                    <CustomCheckbox
                      checked={discountForm.is_active}
                      onChange={(checked) => setDiscountForm({ ...discountForm, is_active: checked })}
                    />
                    Active (customers can use this code)
                  </label>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                  <button
                    type="button"
                    onClick={resetDiscountForm}
                    style={{ padding: '8px 18px', borderRadius: 6, background: '#eee', color: '#333', border: 'none', fontWeight: 'bold', cursor: 'pointer' }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={{ padding: '8px 18px', borderRadius: 6, background: '#000', color: '#fff', border: 'none', fontWeight: 'bold', cursor: 'pointer' }}
                  >
                    {editingDiscount ? 'Save Changes' : 'Create Code'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {discountLoading ? (
            <div style={{ textAlign: 'center', padding: 40 }}>Loading discount codes...</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16 }}>
              <thead>
                <tr style={{ background: '#f5f5f5' }}>
                  <th style={{ padding: 12, border: '1px solid #ddd', textAlign: 'left' }}>Code</th>
                  <th style={{ padding: 12, border: '1px solid #ddd', textAlign: 'left' }}>Discount</th>
                  <th style={{ padding: 12, border: '1px solid #ddd', textAlign: 'left' }}>Limits</th>
                  <th style={{ padding: 12, border: '1px solid #ddd', textAlign: 'left' }}>Status</th>
                  <th style={{ padding: 12, border: '1px solid #ddd', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {discountCodes.length === 0 ? (
                  <tr>
                    <td colSpan="5" style={{ padding: 20, textAlign: 'center', color: '#666' }}>
                      No discount codes yet. Create your first code above.
                    </td>
                  </tr>
                ) : (
                  discountCodes.map((discount) => (
                    <tr key={discount.id}>
                      <td style={{ padding: 12, border: '1px solid #ddd', fontWeight: 700 }}>{discount.code}</td>
                      <td style={{ padding: 12, border: '1px solid #ddd' }}>{formatDiscountValue(discount)}</td>
                      <td style={{ padding: 12, border: '1px solid #ddd', fontSize: 14, color: '#444' }}>{formatDiscountLimits(discount)}</td>
                      <td style={{ padding: 12, border: '1px solid #ddd' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '4px 10px',
                          borderRadius: 999,
                          fontSize: 13,
                          fontWeight: 600,
                          background: discount.status === 'active' ? '#e8f5e8' : '#f3f3f3',
                          color: discount.status === 'active' ? '#1b7f3b' : '#666',
                        }}>
                          {formatDiscountStatusLabel(discount)}
                        </span>
                      </td>
                      <td style={{ padding: 12, border: '1px solid #ddd', textAlign: 'center' }}>
                        <button
                          type="button"
                          onClick={() => openEditDiscountForm(discount)}
                          style={{ padding: '6px 12px', background: '#f0f0f0', color: '#000', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', fontSize: 14, marginRight: 8 }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteDiscountCode(discount.id)}
                          style={{ padding: '6px 12px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 14 }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      )}
      {/* Portfolio Management Section (tab) */}
      {adminTab === 'portfolio' && (
        <div className="admin-section admin-portfolio-wrap">
          <PortfolioAdminSection />
        </div>
      )}
      {adminTab === 'fabrics' && (
        <div className="admin-section">
          <FabricAdminSection />
        </div>
      )}
      {/* Email Templates (stored) Section (tab) */}
      {adminTab === 'email-templates' && (
        <div className="admin-section">
            <p className="admin-panel-desc" style={{ marginTop: 0 }}>
              Store templates for order confirmation, shipping, delivered, and announcements. Preview any template, edit HTML, and send manually or let them send automatically when the trigger occurs.
            </p>
            <div style={{ marginBottom: 20, padding: 12, background: '#f8f9fa', border: '1px solid #eee', borderRadius: 8, fontSize: 13, color: '#333' }}>
              <strong style={{ display: 'block', marginBottom: 8 }}>Template codes (use in subject or HTML)</strong>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px' }}>
                <span><code style={{ background: '#fff', padding: '2px 6px', borderRadius: 4, border: '1px solid #ddd' }}>{'{{customer_name}}'}</code> name</span>
                <span><code style={{ background: '#fff', padding: '2px 6px', borderRadius: 4, border: '1px solid #ddd' }}>{'{{order_number}}'}</code> order #</span>
                <span><code style={{ background: '#fff', padding: '2px 6px', borderRadius: 4, border: '1px solid #ddd' }}>{'{{order_date}}'}</code> date</span>
                <span><code style={{ background: '#fff', padding: '2px 6px', borderRadius: 4, border: '1px solid #ddd' }}>{'{{subtotal}}'}</code> subtotal</span>
                <span><code style={{ background: '#fff', padding: '2px 6px', borderRadius: 4, border: '1px solid #ddd' }}>{'{{shipping_cost}}'}</code> shipping</span>
                <span><code style={{ background: '#fff', padding: '2px 6px', borderRadius: 4, border: '1px solid #ddd' }}>{'{{tax}}'}</code> tax</span>
                <span><code style={{ background: '#fff', padding: '2px 6px', borderRadius: 4, border: '1px solid #ddd' }}>{'{{total}}'}</code> total</span>
                <span><code style={{ background: '#fff', padding: '2px 6px', borderRadius: 4, border: '1px solid #ddd' }}>{'{{items_list}}'}</code> line items (HTML)</span>
                <span><code style={{ background: '#fff', padding: '2px 6px', borderRadius: 4, border: '1px solid #ddd' }}>{'{{shipping_address}}'}</code> street</span>
                <span><code style={{ background: '#fff', padding: '2px 6px', borderRadius: 4, border: '1px solid #ddd' }}>{'{{shipping_city}}'}</code> city</span>
                <span><code style={{ background: '#fff', padding: '2px 6px', borderRadius: 4, border: '1px solid #ddd' }}>{'{{shipping_state}}'}</code> state</span>
                <span><code style={{ background: '#fff', padding: '2px 6px', borderRadius: 4, border: '1px solid #ddd' }}>{'{{shipping_zip}}'}</code> zip</span>
                <span><code style={{ background: '#fff', padding: '2px 6px', borderRadius: 4, border: '1px solid #ddd' }}>{'{{shipping_country}}'}</code> country</span>
                <span><code style={{ background: '#fff', padding: '2px 6px', borderRadius: 4, border: '1px solid #ddd' }}>{'{{tracking_number}}'}</code> tracking #</span>
                <span><code style={{ background: '#fff', padding: '2px 6px', borderRadius: 4, border: '1px solid #ddd' }}>{'{{tracking_url}}'}</code> track link</span>
                <span><code style={{ background: '#fff', padding: '2px 6px', borderRadius: 4, border: '1px solid #ddd' }}>{'{{carrier}}'}</code> carrier</span>
                <span><code style={{ background: '#fff', padding: '2px 6px', borderRadius: 4, border: '1px solid #ddd' }}>{'{{current_year}}'}</code> year</span>
                <span><code style={{ background: '#fff', padding: '2px 6px', borderRadius: 4, border: '1px solid #ddd' }}>{'{{logo_url}}'}</code> logo</span>
                <span><code style={{ background: '#fff', padding: '2px 6px', borderRadius: 4, border: '1px solid #ddd' }}>{'{{subject}}'}</code> / <code style={{ background: '#fff', padding: '2px 6px', borderRadius: 4, border: '1px solid #ddd' }}>{'{{message}}'}</code> / <code style={{ background: '#fff', padding: '2px 6px', borderRadius: 4, border: '1px solid #ddd' }}>{'{{unsubscribe_url}}'}</code> (newsletter)</span>
              </div>
            </div>
            {storedTemplatesLoading && <div style={{ marginBottom: 16 }}>Loading templates...</div>}
            {!storedTemplatesLoading && (
              <>
                <div style={{ marginBottom: 16 }}>
                  <button
                    type="button"
                    onClick={() => setEditingStoredTemplate({ id: null, title: '', trigger: 'manual_only', subject: '', html: '<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;"><h2>{{subject}}</h2><p>{{message}}</p></div>' })}
                    style={{ padding: '10px 20px', fontWeight: 'bold', background: '#000', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}
                  >
                    + Add template
                  </button>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 8, overflow: 'hidden' }}>
                  <thead>
                    <tr style={{ background: '#f5f5f5' }}>
                      <th style={{ padding: 12, textAlign: 'left', borderBottom: '2px solid #eee' }}>Title</th>
                      <th style={{ padding: 12, textAlign: 'left', borderBottom: '2px solid #eee' }}>When it sends</th>
                      <th style={{ padding: 12, textAlign: 'left', borderBottom: '2px solid #eee' }}>Updated</th>
                      <th style={{ padding: 12, textAlign: 'right', borderBottom: '2px solid #eee' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {storedTemplatesList.map(t => (
                      <tr key={t.id} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: 12, fontWeight: 500 }}>{t.title}</td>
                        <td style={{ padding: 12, color: '#666', fontSize: 14 }}>{t.triggerLabel}</td>
                        <td style={{ padding: 12, color: '#666', fontSize: 13 }}>{t.updated_at ? new Date(t.updated_at).toLocaleDateString() : '—'}</td>
                        <td style={{ padding: 12, textAlign: 'right' }}>
                          <button type="button" onClick={async () => {
                            const res = await adminFetch(`${API_BASE}/api/admin/email-templates/${t.id}/preview`, { credentials: 'include' });
                            const data = await res.json();
                            if (data.success) {
                              const w = window.open('', '_blank');
                              if (w) {
                                w.document.write(`<html><head><title>${(data.subject || '').replace(/</g, '&lt;')}</title></head><body style="margin:0;padding:20px;font-family:Arial">${data.html || ''}</body></html>`);
                                w.document.close();
                              }
                            }
                          }} style={{ marginLeft: 6, padding: '6px 10px', fontSize: 12, background: '#f0f0f0', color: '#000', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}>Preview</button>
                          <button type="button" onClick={() => setEditingStoredTemplate({ id: t.id, title: t.title, trigger: t.trigger, subject: t.subject, html: t.html })} style={{ marginLeft: 6, padding: '6px 10px', fontSize: 12, background: '#f0f0f0', color: '#000', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}>Edit</button>
                          <button type="button" onClick={() => { setSendTestEmailForId(t.id); setSendTestEmailAddress(''); }} style={{ marginLeft: 6, padding: '6px 10px', fontSize: 12, background: '#f0f0f0', color: '#000', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}>Send test</button>
                          <button type="button" onClick={() => { setManualSendForId(t.id); setManualSendTo(''); }} style={{ marginLeft: 6, padding: '6px 10px', fontSize: 12, background: '#f0f0f0', color: '#000', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}>Send</button>
                          <button type="button" onClick={async () => {
                            if (!window.confirm(`Delete "${t.title}"?`)) return;
                            await adminFetch(`${API_BASE}/api/admin/email-templates/${t.id}`, { method: 'DELETE', credentials: 'include' });
                            fetchStoredTemplatesList();
                          }} style={{ marginLeft: 6, padding: '6px 10px', fontSize: 12, background: '#ffeaea', color: '#000', border: '1px solid #f5c6cb', borderRadius: 4, cursor: 'pointer' }}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {storedTemplatesList.length === 0 && <p style={{ marginTop: 16, color: '#666' }}>No templates yet. Add one to get started.</p>}
              </>
            )}
            {/* Send test email dialog */}
            {sendTestEmailForId && (
              <div style={{ marginTop: 24, padding: 20, background: '#f8f9fa', borderRadius: 8, border: '1px solid #eee', maxWidth: 400 }}>
                <h4 style={{ marginBottom: 12 }}>Send test email</h4>
                <input type="email" placeholder="your@email.com" value={sendTestEmailAddress} onChange={e => setSendTestEmailAddress(e.target.value)} style={{ width: '100%', padding: 10, marginBottom: 12, borderRadius: 4, border: '1px solid #ddd', boxSizing: 'border-box' }} />
                <div>
                  <button type="button" onClick={async () => {
                    const res = await adminFetch(`${API_BASE}/api/admin/email-templates/${sendTestEmailForId}/send-test`, {
        credentials: 'include',
        method: 'POST', headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ email: sendTestEmailAddress })
                    });
                    const data = await res.json();
                    alert(data.success ? `Sent to ${sendTestEmailAddress}` : (data.error || 'Failed'));
                    if (data.success) setSendTestEmailForId(null);
                  }} style={{ padding: '8px 16px', background: '#000', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', marginRight: 8 }}>Send</button>
                  <button type="button" onClick={() => setSendTestEmailForId(null)} style={{ padding: '8px 16px', background: '#ccc', color: '#000', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Cancel</button>
                </div>
              </div>
            )}
            {/* Manual send dialog */}
            {manualSendForId && (
              <div style={{ marginTop: 24, padding: 20, background: '#f8f9fa', borderRadius: 8, border: '1px solid #eee', maxWidth: 400 }}>
                <h4 style={{ marginBottom: 12 }}>Send email to</h4>
                <input type="email" placeholder="recipient@email.com" value={manualSendTo} onChange={e => setManualSendTo(e.target.value)} style={{ width: '100%', padding: 10, marginBottom: 12, borderRadius: 4, border: '1px solid #ddd', boxSizing: 'border-box' }} />
                <div>
                  <button type="button" onClick={async () => {
                    const res = await adminFetch(`${API_BASE}/api/admin/email-templates/${manualSendForId}/send`, {
        credentials: 'include',
        method: 'POST', headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ to: manualSendTo })
                    });
                    const data = await res.json();
                    alert(data.success ? `Sent to ${manualSendTo}` : (data.error || 'Failed'));
                    if (data.success) setManualSendForId(null);
                  }} style={{ padding: '8px 16px', background: '#000', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', marginRight: 8 }}>Send</button>
                  <button type="button" onClick={() => setManualSendForId(null)} style={{ padding: '8px 16px', background: '#ccc', color: '#000', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Cancel</button>
                </div>
              </div>
            )}
            {/* Add/Edit template form */}
            {editingStoredTemplate && (
              <div style={{ marginTop: 24, padding: 24, background: '#fff', borderRadius: 12, border: '2px solid #000', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
                <h3 style={{ marginBottom: 20 }}>{editingStoredTemplate.id ? 'Edit template' : 'Add template'}</h3>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 'bold', fontSize: 14 }}>Title</label>
                  <input type="text" value={editingStoredTemplate.title} onChange={e => setEditingStoredTemplate({ ...editingStoredTemplate, title: e.target.value })} placeholder="e.g. Order confirmation" style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid #ddd', boxSizing: 'border-box' }} />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 'bold', fontSize: 14 }}>When it sends</label>
                  <select value={editingStoredTemplate.trigger} onChange={e => setEditingStoredTemplate({ ...editingStoredTemplate, trigger: e.target.value })} style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid #ddd', boxSizing: 'border-box', background: '#fff', color: '#000' }}>
                    <option value="order_confirmation" style={{ background: '#fff', color: '#000' }}>Order confirmation (after purchase)</option>
                    <option value="shipping_label_purchased" style={{ background: '#fff', color: '#000' }}>Shipping details (after label purchased)</option>
                    <option value="delivered" style={{ background: '#fff', color: '#000' }}>Delivered</option>
                    <option value="announcement" style={{ background: '#fff', color: '#000' }}>Announcement (manual or broadcast)</option>
                    <option value="manual_only" style={{ background: '#fff', color: '#000' }}>Manual only</option>
                  </select>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 'bold', fontSize: 14 }}>Subject</label>
                  <input type="text" value={editingStoredTemplate.subject} onChange={e => setEditingStoredTemplate({ ...editingStoredTemplate, subject: e.target.value })} placeholder="e.g. Order confirmed - {{order_number}}" style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid #ddd', boxSizing: 'border-box' }} />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 'bold', fontSize: 14 }}>HTML content</label>
                  <p style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>Codes: <code style={{ background: '#f0f0f0', padding: '1px 4px', borderRadius: 3 }}>{'{{customer_name}}'}</code> <code style={{ background: '#f0f0f0', padding: '1px 4px', borderRadius: 3 }}>{'{{order_number}}'}</code> <code style={{ background: '#f0f0f0', padding: '1px 4px', borderRadius: 3 }}>{'{{order_date}}'}</code> <code style={{ background: '#f0f0f0', padding: '1px 4px', borderRadius: 3 }}>{'{{subtotal}}'}</code> <code style={{ background: '#f0f0f0', padding: '1px 4px', borderRadius: 3 }}>{'{{shipping_cost}}'}</code> <code style={{ background: '#f0f0f0', padding: '1px 4px', borderRadius: 3 }}>{'{{tax}}'}</code> <code style={{ background: '#f0f0f0', padding: '1px 4px', borderRadius: 3 }}>{'{{total}}'}</code> <code style={{ background: '#f0f0f0', padding: '1px 4px', borderRadius: 3 }}>{'{{items_list}}'}</code> <code style={{ background: '#f0f0f0', padding: '1px 4px', borderRadius: 3 }}>{'{{shipping_address}}'}</code> <code style={{ background: '#f0f0f0', padding: '1px 4px', borderRadius: 3 }}>{'{{shipping_city}}'}</code> <code style={{ background: '#f0f0f0', padding: '1px 4px', borderRadius: 3 }}>{'{{shipping_state}}'}</code> <code style={{ background: '#f0f0f0', padding: '1px 4px', borderRadius: 3 }}>{'{{shipping_zip}}'}</code> <code style={{ background: '#f0f0f0', padding: '1px 4px', borderRadius: 3 }}>{'{{shipping_country}}'}</code> <code style={{ background: '#f0f0f0', padding: '1px 4px', borderRadius: 3 }}>{'{{tracking_number}}'}</code> <code style={{ background: '#f0f0f0', padding: '1px 4px', borderRadius: 3 }}>{'{{tracking_url}}'}</code> <code style={{ background: '#f0f0f0', padding: '1px 4px', borderRadius: 3 }}>{'{{carrier}}'}</code> <code style={{ background: '#f0f0f0', padding: '1px 4px', borderRadius: 3 }}>{'{{current_year}}'}</code> <code style={{ background: '#f0f0f0', padding: '1px 4px', borderRadius: 3 }}>{'{{logo_url}}'}</code></p>
                  <textarea value={editingStoredTemplate.html} onChange={e => setEditingStoredTemplate({ ...editingStoredTemplate, html: e.target.value })} placeholder="HTML with variables like {{customer_name}}, {{order_number}}" style={{ width: '100%', minHeight: 300, padding: 10, fontFamily: 'monospace', fontSize: 13, borderRadius: 4, border: '1px solid #ddd', boxSizing: 'border-box', resize: 'vertical' }} />
                </div>
                {storedTemplateSaveResult && <div style={{ marginBottom: 12, padding: 10, borderRadius: 6, background: storedTemplateSaveResult.success ? '#e8f5e8' : '#ffeaea', color: storedTemplateSaveResult.success ? '#28a745' : '#dc3545' }}>{storedTemplateSaveResult.message}</div>}
                <div>
                  <button type="button" onClick={async () => {
                    setStoredTemplateSaveResult(null);
                    if (editingStoredTemplate.id) {
                      const res = await adminFetch(`${API_BASE}/api/admin/email-templates/${editingStoredTemplate.id}`, {
        credentials: 'include',
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ title: editingStoredTemplate.title, trigger: editingStoredTemplate.trigger, subject: editingStoredTemplate.subject, html: editingStoredTemplate.html })
                      });
                      const data = await res.json();
                      setStoredTemplateSaveResult({ success: data.success, message: data.success ? 'Saved.' : (data.error || 'Failed') });
                      if (data.success) { fetchStoredTemplatesList(); setEditingStoredTemplate(null); }
                    } else {
                      const res = await adminFetch(`${API_BASE}/api/admin/email-templates`, {
        credentials: 'include',
        method: 'POST', headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ title: editingStoredTemplate.title, trigger: editingStoredTemplate.trigger, subject: editingStoredTemplate.subject, html: editingStoredTemplate.html })
                      });
                      const data = await res.json();
                      setStoredTemplateSaveResult({ success: data.success, message: data.success ? 'Created.' : (data.error || 'Failed') });
                      if (data.success) { fetchStoredTemplatesList(); setEditingStoredTemplate(null); }
                    }
                  }} style={{ padding: '10px 24px', background: '#000', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 'bold', marginRight: 8 }}>Save</button>
                  <button type="button" onClick={() => { setEditingStoredTemplate(null); setStoredTemplateSaveResult(null); }} style={{ padding: '10px 24px', background: '#ccc', color: '#000', border: 'none', borderRadius: 6, cursor: 'pointer' }}>Cancel</button>
                </div>
              </div>
            )}

            {/* Email signups + shipping settings */}
            <div style={{ marginTop: 34 }}>
              {/* Email Signups List */}
              <div style={{ background: '#fff', padding: 40, borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 30 }}>
                  <h2 style={{
                    fontFamily: 'Lucida Grande, Lucida Sans Unicode, Lucida Sans, Geneva, Verdana, sans-serif',
                    fontWeight: 700,
                    color: '#333',
                    margin: 0
                  }}>
                    📧 Email Signups ({emailSignups.length})
                  </h2>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button
                      onClick={downloadEmailSignups}
                      disabled={emailSignups.length === 0}
                      style={{
                        padding: '10px 20px',
                        fontSize: 14,
                        fontWeight: 'bold',
                        background: emailSignups.length === 0 ? '#ccc' : '#28a745',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 6,
                        cursor: emailSignups.length === 0 ? 'not-allowed' : 'pointer'
                      }}
                    >
                      📥 Download CSV
                    </button>
                    <button
                      onClick={fetchEmailSignups}
                      disabled={signupsLoading}
                      style={{
                        padding: '10px 20px',
                        fontSize: 14,
                        fontWeight: 'bold',
                        background: signupsLoading ? '#ccc' : '#333',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 6,
                        cursor: signupsLoading ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {signupsLoading ? 'Loading...' : 'Refresh'}
                    </button>
                  </div>
                </div>

                {signupsError && (
                  <div style={{
                    padding: 12,
                    borderRadius: 6,
                    background: '#ffeaea',
                    color: '#dc3545',
                    border: '1px solid #f5c6cb',
                    marginBottom: 20
                  }}>
                    {signupsError}
                  </div>
                )}

                {signupsLoading && emailSignups.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>
                    Loading email signups...
                  </div>
                ) : emailSignups.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>
                    No email signups yet.
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #ddd' }}>
                          <th style={{ padding: 12, textAlign: 'left', fontWeight: 'bold', color: '#333' }}>Email</th>
                          <th style={{ padding: 12, textAlign: 'left', fontWeight: 'bold', color: '#333' }}>Signed Up</th>
                          <th style={{ padding: 12, textAlign: 'right', fontWeight: 'bold', color: '#333' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {emailSignups.map((signup) => (
                          <tr key={signup.id} style={{ borderBottom: '1px solid #eee' }}>
                            <td style={{ padding: 12, color: '#333' }}>{signup.email}</td>
                            <td style={{ padding: 12, color: '#666' }}>
                              {new Date(signup.created_at).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </td>
                            <td style={{ padding: 12, textAlign: 'right' }}>
                              <button
                                onClick={() => deleteEmailSignup(signup.id)}
                                style={{
                                  padding: '6px 12px',
                                  fontSize: 12,
                                  background: '#dc3545',
                                  color: '#fff',
                                  border: 'none',
                                  borderRadius: 4,
                                  cursor: 'pointer'
                                }}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Shippo Settings */}
              <div style={{ background: '#fff', padding: 40, borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', marginTop: 30 }}>
                <h2 style={{
                  fontFamily: 'Lucida Grande, Lucida Sans Unicode, Lucida Sans, Geneva, Verdana, sans-serif',
                  fontWeight: 700,
                  color: '#333',
                  margin: '0 0 8px 0'
                }}>
                  📦 Shippo Settings (ship-from address)
                </h2>
                <p style={{ color: '#666', marginBottom: 20, fontSize: 14 }}>
                  Set this once. We’ll use it for Shippo labels and automatically add it to the bottom of your emails.
                </p>

                {shippoSettingsResult && (
                  <div style={{
                    padding: 12,
                    borderRadius: 6,
                    background: shippoSettingsResult.success ? '#e8f5e8' : '#ffeaea',
                    color: shippoSettingsResult.success ? '#28a745' : '#dc3545',
                    border: `1px solid ${shippoSettingsResult.success ? '#c3e6c3' : '#f5c6cb'}`,
                    marginBottom: 16
                  }}>
                    {shippoSettingsResult.success ? '✅ ' : '❌ '}
                    {shippoSettingsResult.message}
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 6 }}>Name</label>
                    <input value={shippoSettings.from_name} onChange={e => setShippoSettings(s => ({ ...s, from_name: e.target.value }))} style={{ width: '100%', padding: 10, border: '1px solid #ddd', borderRadius: 6, boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 6 }}>Email</label>
                    <input value={shippoSettings.from_email} onChange={e => setShippoSettings(s => ({ ...s, from_email: e.target.value }))} style={{ width: '100%', padding: 10, border: '1px solid #ddd', borderRadius: 6, boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 6 }}>Phone (required by USPS)</label>
                    <input
                      value={shippoSettings.from_phone}
                      onChange={e => setShippoSettings(s => ({ ...s, from_phone: e.target.value }))}
                      placeholder="e.g. 5551234567"
                      style={{ width: '100%', padding: 10, border: '1px solid #ddd', borderRadius: 6, boxSizing: 'border-box' }}
                    />
                  </div>
                  <div style={{ gridColumn: '1 / span 2' }}>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 6 }}>Street address</label>
                    <input value={shippoSettings.from_street1} onChange={e => setShippoSettings(s => ({ ...s, from_street1: e.target.value }))} style={{ width: '100%', padding: 10, border: '1px solid #ddd', borderRadius: 6, boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 6 }}>City</label>
                    <input value={shippoSettings.from_city} onChange={e => setShippoSettings(s => ({ ...s, from_city: e.target.value }))} style={{ width: '100%', padding: 10, border: '1px solid #ddd', borderRadius: 6, boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 6 }}>State</label>
                    <input value={shippoSettings.from_state} onChange={e => setShippoSettings(s => ({ ...s, from_state: e.target.value }))} style={{ width: '100%', padding: 10, border: '1px solid #ddd', borderRadius: 6, boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 6 }}>ZIP</label>
                    <input value={shippoSettings.from_zip} onChange={e => setShippoSettings(s => ({ ...s, from_zip: e.target.value }))} style={{ width: '100%', padding: 10, border: '1px solid #ddd', borderRadius: 6, boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 6 }}>Country</label>
                    <input value={shippoSettings.from_country} onChange={e => setShippoSettings(s => ({ ...s, from_country: e.target.value }))} style={{ width: '100%', padding: 10, border: '1px solid #ddd', borderRadius: 6, boxSizing: 'border-box' }} />
                  </div>
                </div>

                <div style={{ marginTop: 18, marginBottom: 12, fontWeight: 'bold', color: '#333' }}>Default package (optional)</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                  <div>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 6, fontSize: 12 }}>Length (in)</label>
                    <input value={shippoSettings.parcel_length} onChange={e => setShippoSettings(s => ({ ...s, parcel_length: e.target.value }))} style={{ width: '100%', padding: 10, border: '1px solid #ddd', borderRadius: 6, boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 6, fontSize: 12 }}>Width (in)</label>
                    <input value={shippoSettings.parcel_width} onChange={e => setShippoSettings(s => ({ ...s, parcel_width: e.target.value }))} style={{ width: '100%', padding: 10, border: '1px solid #ddd', borderRadius: 6, boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 6, fontSize: 12 }}>Height (in)</label>
                    <input value={shippoSettings.parcel_height} onChange={e => setShippoSettings(s => ({ ...s, parcel_height: e.target.value }))} style={{ width: '100%', padding: 10, border: '1px solid #ddd', borderRadius: 6, boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 6, fontSize: 12 }}>Weight (lb)</label>
                    <input value={shippoSettings.parcel_weight} onChange={e => setShippoSettings(s => ({ ...s, parcel_weight: e.target.value }))} style={{ width: '100%', padding: 10, border: '1px solid #ddd', borderRadius: 6, boxSizing: 'border-box' }} />
                  </div>
                </div>

                <div style={{ marginTop: 18, display: 'flex', gap: 10 }}>
                  <button
                    type="button"
                    onClick={saveShippoSettings}
                    disabled={shippoSettingsSaving}
                    style={{ padding: '10px 18px', fontWeight: 'bold', background: shippoSettingsSaving ? '#ccc' : '#000', color: '#fff', border: 'none', borderRadius: 6, cursor: shippoSettingsSaving ? 'not-allowed' : 'pointer' }}
                  >
                    {shippoSettingsSaving ? 'Saving...' : 'Save Shippo Settings'}
                  </button>
                  <button
                    type="button"
                    onClick={fetchShippoSettings}
                    disabled={shippoSettingsLoading}
                    style={{ padding: '10px 18px', fontWeight: 'bold', background: shippoSettingsLoading ? '#ccc' : '#333', color: '#fff', border: 'none', borderRadius: 6, cursor: shippoSettingsLoading ? 'not-allowed' : 'pointer' }}
                  >
                    {shippoSettingsLoading ? 'Loading...' : 'Reload'}
                  </button>
                </div>
              </div>
            </div>
        </div>
      )}
      {/* Locked Page Section (tab) */}
      {adminTab === 'locked-page' && (
        <div className="admin-section">
          <div className="admin-panel">
            <h2 className="admin-panel-title">Site Locked Page</h2>
              
              <p className="admin-panel-desc">
                When enabled, visitors will see a "Coming Soon" page with an email signup form instead of the main site.
              </p>

              {lockedError && (
                <div className="admin-message admin-message--error">
                  {lockedError}
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  cursor: 'pointer',
                  fontFamily: 'IBM Plex Mono, monospace',
                  fontSize: 12,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase'
                }}>
                  <input
                    type="checkbox"
                    checked={isLocked}
                    onChange={(e) => updateLockedState(e.target.checked)}
                    disabled={lockedLoading}
                    style={{ marginRight: 12 }}
                  />
                  {isLocked ? 'Site is Locked' : 'Site is Unlocked'}
                </label>
                {lockedLoading && <span style={{ color: '#666', fontSize: 12 }}>Updating…</span>}
              </div>

              {isLocked && (
                <div className="admin-message admin-message--error" style={{ marginTop: 20 }}>
                  Warning: The site is currently locked. Only the admin page is accessible.
                </div>
              )}
          </div>
        </div>
      )}
      {/* Product Management Section (tab) - shared modals */}
      {adminTab === 'products' && (
            <div className="admin-section">
            <div className="admin-toolbar">
              <button type="button" className="admin-btn" onClick={openAddProduct}>Add Product</button>
            </div>
              {productLoading && <div className="admin-message admin-message--loading">Loading…</div>}
              {productError && <div className="admin-message admin-message--error">{productError}</div>}
              <table style={{ width: '100%', marginTop: 8, borderCollapse: 'collapse', fontSize: 15 }}>
                <thead>
                  <tr style={{ background: '#f5f5f5' }}>
                    <th style={{ padding: 8, border: '1px solid #ddd' }}>Name</th>
                    <th style={{ padding: 8, border: '1px solid #ddd' }}>Price</th>
                    <th style={{ padding: 8, border: '1px solid #ddd' }}>Inventory</th>
                    <th style={{ padding: 8, border: '1px solid #ddd' }}>Description</th>
                    <td style={{ padding: 8, border: '1px solid #ddd' }}>Images</td>
                    <th style={{ padding: 8, border: '1px solid #ddd' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map(product => {
                    const images = (product.images && product.images.length > 0)
                      ? product.images
                      : (product.image ? [product.image] : []);
                    const totalInventory = product.total_inventory || 0;
                    const inventoryStatus = totalInventory > 0 ? 
                      <span style={{ color: '#28a745', fontWeight: 'bold' }}>In Stock ({totalInventory})</span> : 
                      <span style={{ color: '#dc3545', fontWeight: 'bold' }}>Out of Stock</span>;
                    
                    // Create inventory breakdown
                    const sizeBreakdown = product.sizes && product.sizes.length > 0 ? (
                      <div style={{ fontSize: 12, marginTop: 4 }}>
                        {product.sizes.map((size, idx) => (
                          <span key={idx} style={{ 
                            marginRight: 8, 
                            padding: '2px 6px', 
                            background: size.quantity > 0 ? '#e8f5e8' : '#ffe8e8', 
                            borderRadius: 3,
                            color: size.quantity > 0 ? '#28a745' : '#dc3545',
                            fontWeight: 'bold'
                          }}>
                            {size.size}: {size.quantity}
                          </span>
                        ))}
                      </div>
                    ) : null;
                    
                    return (
                      <tr key={product.id} style={{ 
                        background: totalInventory === 0 ? '#fff5f5' : 'transparent',
                        opacity: totalInventory === 0 ? 0.7 : 1
                      }}>
                        <td style={{ padding: 8, border: '1px solid #ddd' }}>{product.name}</td>
                        <td style={{ padding: 8, border: '1px solid #ddd' }}>${Number(product.price).toFixed(2)}</td>
                        <td style={{ padding: 8, border: '1px solid #ddd' }}>
                          {inventoryStatus}
                          {sizeBreakdown}
                        </td>
                        <td style={{ padding: 8, border: '1px solid #ddd' }}>
                          <div 
                            style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} 
                            dangerouslySetInnerHTML={{ __html: sanitizeProductDescription(stripBackgroundColor(product.description || '')) }}
                          />
                        </td>
                        <td style={{ padding: 8, border: '1px solid #ddd' }}>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {images.map((img, idx) => (
                              <img key={img} src={getUrl(img)} alt='' style={{ maxWidth: 40, maxHeight: 40, borderRadius: 4, border: idx === 0 ? '2px solid #000' : '1px solid #ccc', boxShadow: 'none' }} title={idx === 0 ? 'Main image' : ''} />
                            ))}
                          </div>
                        </td>
                        <td style={{ padding: 8, border: '1px solid #ddd' }}>
                          <button onClick={() => openEditProduct(product)} style={{ marginRight: 8, background: '#000', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 18px', fontWeight: 'bold', cursor: 'pointer' }}>Edit</button>
                          <button onClick={() => openSizeManager(product)} style={{ marginRight: 8, background: '#007bff', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 18px', fontWeight: 'bold', cursor: 'pointer' }}>Sizes</button>
                          <button onClick={() => handleDeleteProduct(product.id)} style={{ background: '#dc3545', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 18px', fontWeight: 'bold', cursor: 'pointer' }}>Delete</button>
                        </td>
                      </tr>
                    );
                  })}
                  {products.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', color: '#888' }}>No products found.</td></tr>}
                </tbody>
              </table>
              {/* Product Form Modal */}
              {showProductForm && (
                <div className="admin-modal-overlay">
                  <div className="admin-modal">
                    <h3 style={{ marginTop: 0 }}>{editingProduct ? 'Edit Product' : 'Add Product'}</h3>
                    <form onSubmit={handleProductFormSubmit}>
                      <div style={{ marginBottom: 14 }}>
                        <label>Name:</label><br />
                        <input type='text' value={productForm.name} onChange={e => setProductForm(f => ({ ...f, name: e.target.value }))} style={{ width: '100%', padding: 8, fontSize: 16, color: '#000' }} required />
                      </div>
                      <div style={{ marginBottom: 14 }}>
                        <label>Slug:</label><br />
                        <input type='text' value={productForm.slug || ''} onChange={e => setProductForm(f => ({ ...f, slug: e.target.value.replace(/\s+/g, '-').toLowerCase() }))} style={{ width: '100%', padding: 8, fontSize: 16, color: '#000' }} required pattern='[a-z0-9\-]+' title='Lowercase letters, numbers, and dashes only' />
                        <div style={{ fontSize: 12, color: '#888' }}>URL-friendly, lowercase, no spaces (e.g. "void-ring")</div>
                      </div>
                      <div style={{ marginBottom: 14 }}>
                        <label>Price:</label><br />
                        <input type='number' step='0.01' value={productForm.price} onChange={e => setProductForm(f => ({ ...f, price: e.target.value }))} style={{ width: '100%', padding: 8, fontSize: 16, color: '#000' }} required />
                      </div>
                      {/* Remove highlight dropdown */}
                      {/* <div style={{ marginBottom: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
                        <label style={{ fontWeight: 'bold', marginRight: 8 }}>Highlight:</label>
                        <select
                          onChange={e => {
                            const value = e.target.value;
                            let newState = editorState;
                            // Remove all highlight styles first
                            highlightColors.forEach(hc => {
                              if (editorState.getCurrentInlineStyle().has(hc.style)) {
                                newState = RichUtils.toggleInlineStyle(newState, hc.style);
                              }
                            });
                            if (value && value !== 'NONE') {
                              newState = RichUtils.toggleInlineStyle(newState, value);
                            }
                            setEditorState(EditorState.forceSelection(newState, newState.getSelection()));
                          }}
                          defaultValue="NONE"
                          style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc', fontWeight: 'bold' }}
                        >
                          <option value="NONE">No highlight</option>
                          {highlightColors.map(hc => (
                            <option key={hc.style} value={hc.style}>{hc.name}</option>
                          ))}
                        </select>
                      </div> */}
                      <div style={{ marginBottom: 14 }}>
                        <label>Description:</label><br />
                        <div style={{ border: '1px solid #ccc', borderRadius: 4, minHeight: 120 }}>
                          <Editor
                            editorState={editorState}
                            onEditorStateChange={setEditorState}
                            toolbar={{
                              options: ['inline', 'blockType', 'fontSize', 'list', 'textAlign', 'history'],
                              inline: { options: ['bold', 'italic', 'underline', 'strikethrough'] },
                              textAlign: { options: ['left', 'center', 'right', 'justify'] }
                            }}
                            editorStyle={{ minHeight: 100, padding: 8, background: 'transparent' }}
                          />
                        </div>
                      </div>
                      <div style={{ marginBottom: 14, padding: 14, background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8 }}>
                        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 6 }}>Internal notes (admin only)</label>
                        <p style={{ margin: '0 0 8px 0', fontSize: 12, color: '#92400e' }}>
                          Not shown on the storefront. For your reference while managing this product.
                        </p>
                        <textarea
                          value={productForm.admin_notes || ''}
                          onChange={e => setProductForm(f => ({ ...f, admin_notes: e.target.value }))}
                          placeholder="e.g. supplier SKU, production notes, customer requests…"
                          rows={5}
                          style={{ width: '100%', padding: 10, fontSize: 14, color: '#000', borderRadius: 4, border: '1px solid #d97706', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
                        />
                      </div>
                      <div style={{ marginBottom: 18 }}>
                        <label>Images:</label><br />
                        <div
                          onDrop={handleImageDrop}
                          onDragOver={handleImageDragOver}
                          onDragEnter={handleImageDragEnter}
                          style={{ marginTop: 8, padding: 16, border: '2px dashed #aaa', borderRadius: 6, textAlign: 'center', background: '#fafafa' }}
                        >
                          <span onClick={() => productImageInputRef.current?.click()} style={{ cursor: 'pointer', display: 'block', marginBottom: 8 }}>
                            Drag & drop images here (or click to select multiple)
                          </span>
                          <p style={{ margin: '0 0 8px', fontSize: 12, color: '#666' }}>
                            JPEG, PNG, GIF, or WebP. No limit on count — first image is the main photo.
                            {(productForm.images || []).length > 0 ? ` (${productForm.images.length} attached)` : ''}
                          </p>
                          <input
                            ref={productImageInputRef}
                            type='file'
                            accept='image/jpeg,image/png,image/gif,image/webp,.jpg,.jpeg,.png,.gif,.webp'
                            multiple
                            style={{ display: 'none' }}
                            onChange={e => {
                              if (e.target.files?.length) {
                                handleImageFiles(e.target.files);
                                e.target.value = '';
                              }
                            }}
                          />
                          {/* Carousel for images */}
                          <AdminImageCarousel
                            images={productForm.images || []}
                            onRemove={handleRemoveImage}
                            onSetMain={handleSetMainImage}
                            onDragStart={handleImageDragStart}
                            onDropReorder={handleImageDropReorder}
                            onFilesDrop={handleImageFiles}
                          />
                        </div>
                      </div>
                      {/* Category selection in product form */}
                      <div style={{ marginBottom: 18 }}>
                        <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
                          Categories:
                        </label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {categories.map(cat => {
                            const catId = String(cat.id || cat._id);
                            return (
                              <label key={catId} style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 'normal' }}>
                                <input
                                  type="checkbox"
                                  checked={productForm.category_ids?.some(id => String(id) === catId)}
                                  onChange={e => {
                                    const checked = e.target.checked;
                                    setProductForm(f => {
                                      let newIds = Array.isArray(f.category_ids) ? [...f.category_ids] : [];
                                      // Convert all IDs to strings for consistency
                                      newIds = newIds.map(id => String(id));
                                      if (checked) {
                                        if (!newIds.includes(catId)) newIds.push(catId);
                                      } else {
                                        newIds = newIds.filter(id => id !== catId);
                                      }
                                      return { ...f, category_ids: newIds };
                                    });
                                  }}
                                />
                                {cat.name}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                      
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                        <button type='button' onClick={closeProductForm} style={{ padding: '8px 18px', borderRadius: 6, background: '#eee', color: '#333', border: 'none', fontWeight: 'bold', cursor: 'pointer' }}>Cancel</button>
                        <button type='submit' style={{ padding: '8px 18px', borderRadius: 6, background: '#000', color: '#fff', border: 'none', fontWeight: 'bold', cursor: 'pointer' }}>{editingProduct ? 'Save Changes' : 'Add Product'}</button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {/* Size Manager Modal */}
              {showSizeManager && (
                <div className="admin-modal-overlay">
                  <div className="admin-modal" style={{ minWidth: 500, maxWidth: 700 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                      <h3 style={{ margin: 0 }}>Size & Inventory Management</h3>
                      <button onClick={closeSizeManager} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#666' }}>×</button>
                    </div>
                    
                    <div style={{ marginBottom: 20, padding: 16, background: '#f8f8f8', borderRadius: 8, border: '1px solid #eee' }}>
                      <h4 style={{ margin: '0 0 12px 0' }}>{editingProductSizes?.name}</h4>
                      <p style={{ margin: 0, color: '#666' }}>
                        Manage sizes and inventory quantities for this product
                      </p>
                    </div>

                    {/* Add New Size Form */}
                    <div style={{ marginBottom: 24, padding: 20, background: '#f8f8f8', borderRadius: 8, border: '1px solid #eee' }}>
                      <h4 style={{ margin: '0 0 16px 0' }}>Add New Size</h4>
                      <form onSubmit={handleAddSize} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
                          <div>
                            <label style={{ display: 'block', marginBottom: 6, fontWeight: 'bold', fontSize: 14 }}>Size:</label>
                            <input
                              type="text"
                              value={sizeForm.size}
                              onChange={e => setSizeForm(f => ({ ...f, size: e.target.value }))}
                              placeholder="e.g., S, M, L, 7, 8, 9"
                              style={{ padding: 8, fontSize: 16, borderRadius: 4, border: '1px solid #ccc', width: 100, color: '#000' }}
                              required
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', marginBottom: 6, fontWeight: 'bold', fontSize: 14 }}>Quantity:</label>
                            <input
                              type="number"
                              value={sizeForm.quantity}
                              onChange={e => setSizeForm(f => ({ ...f, quantity: parseInt(e.target.value) || 0 }))}
                              min="0"
                              style={{ padding: 8, fontSize: 16, borderRadius: 4, border: '1px solid #ccc', width: 100, color: '#000' }}
                              required
                            />
                          </div>
                          <button
                            type="submit"
                            disabled={sizeLoading}
                            style={{ padding: '8px 16px', background: '#000', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 'bold', cursor: 'pointer' }}
                          >
                            {sizeLoading ? 'Adding...' : 'Add Size'}
                          </button>
                        </div>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <CustomCheckbox
                              checked={sizeForm.preorder || false}
                              onChange={(checked) => setSizeForm(f => ({ ...f, preorder: checked }))}
                            />
                            <span style={{ fontSize: 14, fontWeight: 'bold', cursor: 'pointer' }} onClick={() => setSizeForm(f => ({ ...f, preorder: !f.preorder }))}>Preorder Only</span>
                          </div>
                          <p style={{ margin: '8px 0 0 28px', fontSize: 12, color: '#666' }}>
                            Check this if this size is for preorder only
                          </p>
                        </div>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <CustomCheckbox
                              checked={sizeForm.made_to_order || false}
                              onChange={(checked) => setSizeForm(f => ({ ...f, made_to_order: checked }))}
                            />
                            <span style={{ fontSize: 14, fontWeight: 'bold', cursor: 'pointer' }} onClick={() => setSizeForm(f => ({ ...f, made_to_order: !f.made_to_order }))}>Made to Order</span>
                          </div>
                          <p style={{ margin: '8px 0 0 28px', fontSize: 12, color: '#666' }}>
                            Check this if this size is made-to-order
                          </p>
                        </div>
                      </form>
                      {sizeError && <div style={{ color: 'red', marginTop: 8, fontSize: 14 }}>{sizeError}</div>}
                    </div>

                    {/* Current Sizes Table */}
                    <div>
                      <h4 style={{ margin: '0 0 16px 0' }}>Current Sizes & Inventory</h4>
                      
                      {/* Color Legend */}
                      <div style={{ 
                        marginBottom: 16, 
                        padding: 12, 
                        background: '#f8f9fa', 
                        borderRadius: 6, 
                        border: '1px solid #dee2e6',
                        fontSize: 12
                      }}>
                        <div style={{ fontWeight: 'bold', marginBottom: 6 }}>Legend:</div>
                        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#28a745', border: '2px solid #fff', boxShadow: '0 0 0 1px #ddd' }} />
                            <span>In Stock ({'>'}5 units)</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#ffc107', border: '2px solid #fff', boxShadow: '0 0 0 1px #ddd' }} />
                            <span>Low Stock (1-5 units)</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#dc3545', border: '2px solid #fff', boxShadow: '0 0 0 1px #ddd' }} />
                            <span>Out of Stock (0 units)</span>
                          </div>
                        </div>
                      </div>
                      {editingProductSizes?.sizes && editingProductSizes.sizes.length > 0 ? (
                        <>
                          {/* Inventory Summary */}
                          <div style={{ 
                            marginBottom: 16, 
                            padding: 16, 
                            background: (() => {
                              const total = editingProductSizes.sizes.reduce((sum, s) => sum + s.quantity, 0);
                              const lowStock = editingProductSizes.sizes.some(s => s.quantity > 0 && s.quantity <= 5);
                              if (total === 0) return '#ffe6e6';
                              if (lowStock) return '#fff3cd';
                              return '#e8f5e8';
                            })(),
                            borderRadius: 8, 
                            border: (() => {
                              const total = editingProductSizes.sizes.reduce((sum, s) => sum + s.quantity, 0);
                              const lowStock = editingProductSizes.sizes.some(s => s.quantity > 0 && s.quantity <= 5);
                              if (total === 0) return '1px solid #f5c6cb';
                              if (lowStock) return '1px solid #ffeaa7';
                              return '1px solid #c3e6c3';
                            })()
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
                              <div>
                                <strong>Total Inventory:</strong> {editingProductSizes.sizes.reduce((sum, s) => sum + s.quantity, 0)} units
                              </div>
                              <div>
                                <strong>Active Sizes:</strong> {editingProductSizes.sizes.filter(s => s.quantity > 0).length} of {editingProductSizes.sizes.length}
                              </div>
                              <div>
                                <strong>Status:</strong> 
                                {(() => {
                                  const total = editingProductSizes.sizes.reduce((sum, s) => sum + s.quantity, 0);
                                  const lowStock = editingProductSizes.sizes.some(s => s.quantity > 0 && s.quantity <= 5);
                                  if (total === 0) return <span style={{ color: '#dc3545', fontWeight: 'bold' }}> Out of Stock</span>;
                                  if (lowStock) return <span style={{ color: '#856404', fontWeight: 'bold' }}> Low Stock</span>;
                                  return <span style={{ color: '#28a745', fontWeight: 'bold' }}> In Stock</span>;
                                })()}
                              </div>
                            </div>
                            {/* Low Stock Warning */}
                            {editingProductSizes.sizes.some(s => s.quantity > 0 && s.quantity <= 5) && (
                              <div style={{ 
                                marginTop: 12, 
                                padding: 8, 
                                background: '#fff3cd', 
                                borderRadius: 4, 
                                border: '1px solid #ffeaa7',
                                color: '#856404',
                                fontSize: 14
                              }}>
                                ⚠️ Some sizes have low stock (≤5 units). Consider restocking soon.
                              </div>
                            )}
                            {/* Out of Stock Warning */}
                            {editingProductSizes.sizes.some(s => s.quantity === 0) && (
                              <div style={{ 
                                marginTop: 8, 
                                padding: 8, 
                                background: '#ffe6e6', 
                                borderRadius: 4, 
                                border: '1px solid #f5c6cb',
                                color: '#721c24',
                                fontSize: 14
                              }}>
                                ❌ Some sizes are out of stock. These won't appear on the homepage.
                              </div>
                            )}
                          </div>
                          
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                            <thead>
                              <tr style={{ background: '#f5f5f5' }}>
                                <th style={{ padding: 12, border: '1px solid #ddd', textAlign: 'left' }}>Size</th>
                                <th style={{ padding: 12, border: '1px solid #ddd', textAlign: 'left' }}>Quantity</th>
                                <th style={{ padding: 12, border: '1px solid #ddd', textAlign: 'center' }}>Preorder</th>
                                <th style={{ padding: 12, border: '1px solid #ddd', textAlign: 'center' }}>Made to Order</th>
                                <th style={{ padding: 12, border: '1px solid #ddd', textAlign: 'center' }}>Status</th>
                                <th style={{ padding: 12, border: '1px solid #ddd', textAlign: 'center' }}>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {editingProductSizes.sizes.map((sizeData, index) => (
                                <tr key={index} style={{ 
                                  background: (() => {
                                    if (sizeData.quantity === 0) return '#fff5f5';
                                    if (sizeData.quantity <= 5) return '#fff8dc';
                                    return 'transparent';
                                  })()
                                }}>
                                  <td style={{ padding: 12, border: '1px solid #ddd', fontWeight: 'bold' }}>{sizeData.size}</td>
                                  <td style={{ padding: 12, border: '1px solid #ddd' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <input
                                      type="number"
                                      value={sizeData.quantity}
                                      onChange={e => {
                                        const newQuantity = parseInt(e.target.value) || 0;
                                        if (newQuantity >= 0) {
                                          handleUpdateInventory(editingProductSizes.id, sizeData.size, newQuantity);
                                        }
                                      }}
                                      min="0"
                                      style={{ 
                                        padding: 6, 
                                        fontSize: 14, 
                                        borderRadius: 4, 
                                        border: '1px solid #ccc', 
                                        width: 80,
                                          color: '#000',
                                          background: (() => {
                                            if (sizeData.quantity === 0) return '#fff5f5';
                                            if (sizeData.quantity <= 5) return '#fff8dc';
                                            return '#fff';
                                          })()
                                        }}
                                      />
                                      {/* Inventory Level Indicator */}
                                      <div style={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: '50%',
                                        background: (() => {
                                          if (sizeData.quantity === 0) return '#dc3545';
                                          if (sizeData.quantity <= 5) return '#ffc107';
                                          return '#28a745';
                                        })(),
                                        border: '2px solid #fff',
                                        boxShadow: '0 0 0 1px #ddd'
                                      }} />
                                    </div>
                                  </td>
                                  <td style={{ padding: 12, border: '1px solid #ddd', textAlign: 'center' }}>
                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                      <CustomCheckbox
                                        checked={sizeData.preorder || false}
                                        onChange={(checked) => handleUpdatePreorder(editingProductSizes.id, sizeData.size, checked)}
                                      />
                                    </div>
                                  </td>
                                  <td style={{ padding: 12, border: '1px solid #ddd', textAlign: 'center' }}>
                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                      <CustomCheckbox
                                        checked={sizeData.made_to_order || false}
                                        onChange={(checked) => handleUpdateMadeToOrder(editingProductSizes.id, sizeData.size, checked)}
                                      />
                                    </div>
                                  </td>
                                  <td style={{ padding: 12, border: '1px solid #ddd', textAlign: 'center' }}>
                                    {(() => {
                                      if (sizeData.quantity === 0) return <span style={{ color: '#dc3545', fontWeight: 'bold' }}>Out of Stock</span>;
                                      if (sizeData.quantity <= 5) return <span style={{ color: '#856404', fontWeight: 'bold' }}>Low Stock</span>;
                                      return <span style={{ color: '#28a745', fontWeight: 'bold' }}>In Stock</span>;
                                    })()}
                                  </td>
                                  <td style={{ padding: 12, border: '1px solid #ddd', textAlign: 'center' }}>
                                    <button
                                      onClick={() => handleDeleteSize(editingProductSizes.id, sizeData.size)}
                                      style={{ padding: '6px 12px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
                                    >
                                      Delete
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </>
                      ) : (
                        <div style={{ textAlign: 'center', padding: 40, color: '#666', background: '#f8f8f8', borderRadius: 8, border: '1px solid #eee' }}>
                          No sizes configured yet. Add your first size above.
                        </div>
                      )}
                    </div>

                    {/* Quick Actions */}
                    <div style={{ marginTop: 24, padding: 20, background: '#f8f8f8', borderRadius: 8, border: '1px solid #eee' }}>
                      <h4 style={{ margin: '0 0 16px 0' }}>Quick Actions</h4>
                      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        <button
                          onClick={() => {
                            const sizes = [
                              { size: 'XS', quantity: 10 },
                              { size: 'S', quantity: 10 },
                              { size: 'M', quantity: 10 },
                              { size: 'L', quantity: 10 },
                              { size: 'XL', quantity: 10 }
                            ];
                            handleAddMultipleSizes(sizes);
                          }}
                          style={{ padding: '8px 16px', background: '#007bff', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 'bold', cursor: 'pointer' }}
                        >
                          Add Standard Sizes (XS-XL)
                        </button>
                        <button
                          onClick={() => {
                            const sizes = [
                              { size: '7', quantity: 10 },
                              { size: '8', quantity: 10 },
                              { size: '9', quantity: 10 },
                              { size: '10', quantity: 10 },
                              { size: '11', quantity: 10 }
                            ];
                            handleAddMultipleSizes(sizes);
                          }}
                          style={{ padding: '8px 16px', background: '#28a745', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 'bold', cursor: 'pointer' }}
                        >
                          Add Shoe Sizes (7-11)
                        </button>
                        <button
                          onClick={() => {
                            const sizes = [
                              { size: 'One Size', quantity: 10 }
                            ];
                            handleAddMultipleSizes(sizes);
                          }}
                          style={{ padding: '8px 16px', background: '#6f42c1', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 'bold', cursor: 'pointer' }}
                        >
                          Add One Size
                        </button>
                        <button
                          onClick={() => {
                            if (editingProductSizes?.sizes && editingProductSizes.sizes.length > 0) {
                              const newSizes = editingProductSizes.sizes.map(s => ({ ...s, quantity: 10 }));
                              handleBulkInventoryUpdate(newSizes);
                            }
                          }}
                          style={{ padding: '8px 16px', background: '#fd7e14', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 'bold', cursor: 'pointer' }}
                        >
                          Set All Sizes to 10
                        </button>
                        <button
                          onClick={() => {
                            if (editingProductSizes?.sizes && editingProductSizes.sizes.length > 0) {
                              const newSizes = editingProductSizes.sizes.map(s => ({ ...s, quantity: 0 }));
                              handleBulkInventoryUpdate(newSizes);
                            }
                          }}
                          style={{ padding: '8px 16px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 'bold', cursor: 'pointer' }}
                        >
                          Set All Sizes to 0
                        </button>
                        <button
                          onClick={() => {
                            if (editingProductSizes?.sizes && editingProductSizes.sizes.length > 0) {
                              const newSizes = editingProductSizes.sizes.map(s => ({ ...s, quantity: 25 }));
                              handleBulkInventoryUpdate(newSizes);
                            }
                          }}
                          style={{ padding: '8px 16px', background: '#17a2b8', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 'bold', cursor: 'pointer' }}
                        >
                          Set All Sizes to 25
                        </button>
                        <button
                          onClick={() => {
                            if (editingProductSizes?.sizes && editingProductSizes.sizes.length > 0) {
                              const newSizes = editingProductSizes.sizes.map(s => ({ ...s, quantity: s.quantity + 5 }));
                              handleBulkInventoryUpdate(newSizes);
                            }
                          }}
                          style={{ padding: '8px 16px', background: '#20c997', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 'bold', cursor: 'pointer' }}
                        >
                          Add 5 to All Sizes
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          </div>
        </main>
      </div>
    </div>
    <style>{`
      input[type="checkbox"] {
        width: 20px !important;
        height: 20px !important;
        min-width: 20px !important;
        min-height: 20px !important;
        cursor: pointer !important;
        accent-color: #000 !important;
        margin: 0 !important;
        padding: 0 !important;
        vertical-align: middle !important;
      }
      input[type="checkbox"]:checked {
        accent-color: #000 !important;
      }
    `}</style>
    </>
  );
}

